import * as tslib_1 from "tslib";
// Core packages
import { formatDate } from "@angular/common";
import { AfterViewInit, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
// Third party packages
import { MatDialog, MatPaginator, MatSort } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { AlignmentType, Document, HeadingLevel, HorizontalPositionAlign, HorizontalPositionRelativeFrom, Media, Packer, Paragraph, TextRun, UnderlineType, VerticalPositionAlign, VerticalPositionRelativeFrom } from "docx";
import Docxtemplater from 'docxtemplater';
import * as FileSaver from "file-saver";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils/index.js';
import { combineLatest, fromEvent, merge } from "rxjs";
import { concatMap, debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from "rxjs/operators";
// Custom packages
import { ViewPlatformNotesDialogComponent } from 'src/app/components/platform-note-card/components/view-platform-notes-dialog/view-platform-notes-dialog.component';
import { PlatformNoteCardComponent } from 'src/app/components/platform-note-card/platform-note-card.component';
import { AccessLogsService } from 'src/app/modules/access-logs/access-logs.service';
import { AuthService } from "src/app/modules/auth/auth.service";
import { BalancesService } from "src/app/modules/balances/balances.service";
import { CommunicationsService } from "src/app/modules/communications/communications.service";
import { ADDITIONAL_SERVICE_FLAGS, additionalServiceFee, bindServiceLinks, isAdditionalServiceActive } from "src/app/modules/companies/additional-services";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { FireSafetyDocsAddDocDialogComponent } from "src/app/modules/companies/components/add-fire-safety-doc-dialog/add-fire-safety-doc-dialog.component";
import { AddPhonebookContactDialogComponent } from "src/app/modules/companies/components/add-phonebook-dialog/add-phonebook-dialog.component";
import { AddSubUserDialogComponent } from "src/app/modules/companies/components/add-sub-user-dialog/add-sub-user-dialog.component";
import { FireSafetyDocsEditDocDialogComponent } from "src/app/modules/companies/components/edit-fire-safety-doc-dialog/edit-fire-safety-doc-dialog.component";
import { EditPhonebookContactDialogComponent } from "src/app/modules/companies/components/edit-phonebook-dialog/edit-phonebook-dialog.component";
import { FireSafetyDocsQrCodeDialogComponent } from "src/app/modules/companies/components/fire-safety-qr-code-dialog/fire-safety-qr-code-dialog.component";
import { GeneratePrivacyDocsDialogComponent } from 'src/app/modules/companies/components/generate-privacy-docs-dialog/generate-privacy-docs-dialog.component';
import { PrintQrcodeDialogComponent } from 'src/app/modules/companies/components/print-qrcode-dialog/print-qrcode-dialog.component';
import { PrivacyStudioDocsAddDocDialogComponent } from 'src/app/modules/companies/components/add-privacy-studio-doc-dialog/add-privacy-studio-doc-dialog.component';
import { PrivacyStudioDocsEditDocDialogComponent } from 'src/app/modules/companies/components/edit-privacy-studio-doc-dialog/edit-privacy-studio-doc-dialog.component';
import { PrivacyDocsLinkDialogComponent } from 'src/app/modules/companies/components/privacy-docs-link-dialog/privacy-docs-link-dialog.component';
import { PrivacyDocsQrCodeDialogComponent } from 'src/app/modules/companies/components/privacy-docs-qr-code-dialog/privacy-docs-qr-code-dialog.component';
import { PrivacyStudioDocsQrCodeDialogComponent } from 'src/app/modules/companies/components/privacy-studio-docs-qr-code-dialog/privacy-studio-docs-qr-code-dialog.component';
import { StudioPrivacyTemplatesDialogComponent } from 'src/app/modules/companies/components/studio-privacy-templates-dialog/studio-privacy-templates-dialog.component';
import { GenericDocsAddDocDialogComponent } from 'src/app/modules/generic-docs/components/add-doc-dialog/add-doc-dialog.component';
import { GenericDocsEditDocDialogComponent } from 'src/app/modules/generic-docs/components/edit-doc-dialog/edit-doc-dialog.component';
import { GenericDocsQrCodeDialogComponent } from 'src/app/modules/generic-docs/components/qr-code-dialog/qr-code-dialog.component';
import { GenericDocsService } from 'src/app/modules/generic-docs/generic-docs.service';
import { PricesService } from "src/app/modules/prices/prices.service";
import { DocumentDownloadDialogComponent } from 'src/app/modules/properties/components/document-download-dialog/document-download-dialog.component';
import { SendEmailDialogComponent } from "src/app/modules/properties/components/send-email-dialog/send-email-dialog.component";
import { DocumentArchiveExportDownloadService } from "src/app/modules/properties/document-archive-export-download.service";
import { documentArchiveExportTargetOf, legacyPageExportMenu, } from "src/app/modules/properties/document-archive-export.utils";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { RolesService } from 'src/app/modules/roles/roles.service';
import { SubscriptionsService } from "src/app/modules/subscriptions/subscriptions.service";
import { SuppliersService } from 'src/app/modules/suppliers/suppliers.service';
import { UsersService } from "src/app/modules/users/users.service";
import { AddCommunicationDialogComponent } from "src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { InfoDialogComponent } from 'src/app/shared/components/dialogs/info-dialog/info-dialog.component';
import { VideoDialogComponent } from 'src/app/shared/components/dialogs/video-dialog/video-dialog.component';
import { Company } from "src/app/shared/models/company/company.model";
import { ConfigService } from "src/app/shared/services/config.service";
import { FireSafetyDocsService } from 'src/app/shared/services/fire-safety-docs.service';
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { DEFAULT_PRIVACY_DOC_EXTENSION, PRIVACY_DOC_MIME_EXTENSIONS, PrivacyDocsService, PROPERTY_PRIVACY_TEMPLATE, SUPPLIER_PRIVACY_TEMPLATE, } from 'src/app/shared/services/privacy-docs.service';
import { PrivacyStudioDocsService } from 'src/app/shared/services/privacy-studio-docs.service';
import { InformativeNoteService } from 'src/app/modules/companies/informative-note.service';
import { ThemeService } from "src/app/shared/services/theme.service";
import { VideoViewService } from "src/app/shared/services/video-view.service";
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";
import { AccessLogsDataSource } from 'src/app/shared/utils/data-sources/access-logs-data-source';
import { BalancesDataSource } from "src/app/shared/utils/data-sources/balances-data-source";
import { FireSafetyDocsDataSource } from 'src/app/shared/utils/data-sources/fire-safety-docs-source';
import { GenericDocsDataSource } from 'src/app/shared/utils/data-sources/generic-docs-source';
import { PhonebookDataSource } from "src/app/shared/utils/data-sources/phonebook-data-source";
import { PrivacyDocsDataSource } from 'src/app/shared/utils/data-sources/privacy-docs-source';
import { PrivacyStudioDocsDataSource } from 'src/app/shared/utils/data-sources/privacy-studio-docs-source';
import { SubPropertiesDataSource } from "src/app/shared/utils/data-sources/sub-properties-data-source";
import { SubUsersDataSource } from "src/app/shared/utils/data-sources/sub-users-data-source";
import { SuppliersInvitationDataSource } from "src/app/shared/utils/data-sources/suppliers-invitation-data-source";
import { SuppliersNewDataSource } from 'src/app/shared/utils/data-sources/suppliers-new-data-source';
import { VideoViewDataSource } from "src/app/shared/utils/data-sources/video-view-data-source";
var CompanyDetailComponent = /** @class */ (function () {
    /** Only "Nota informativa" (no Privacy / Privacy SAG): supplier PEC sending is not part of it */
    /**
     * Class constructor
     */
    function CompanyDetailComponent(configService, activatedRoute, companiesService, router, toastrService, helperService, translateService, themeService, authService, dialog, newMessageDialog, cookieService, pricesService, subscriptionsService, balancesService, listsService, propertiesService, accessLogsService, rolesService, usersService, videoViewService, genericDocsService, fireSafetyDocsService, privacyDocsService, privacyStudioDocsService, communicationsService, suppliersService, informativeNoteService, documentArchiveExportDownloadService) {
        this.configService = configService;
        this.activatedRoute = activatedRoute;
        this.companiesService = companiesService;
        this.router = router;
        this.toastrService = toastrService;
        this.helperService = helperService;
        this.translateService = translateService;
        this.themeService = themeService;
        this.authService = authService;
        this.dialog = dialog;
        this.newMessageDialog = newMessageDialog;
        this.cookieService = cookieService;
        this.pricesService = pricesService;
        this.subscriptionsService = subscriptionsService;
        this.balancesService = balancesService;
        this.listsService = listsService;
        this.propertiesService = propertiesService;
        this.accessLogsService = accessLogsService;
        this.rolesService = rolesService;
        this.usersService = usersService;
        this.videoViewService = videoViewService;
        this.genericDocsService = genericDocsService;
        this.fireSafetyDocsService = fireSafetyDocsService;
        this.privacyDocsService = privacyDocsService;
        this.privacyStudioDocsService = privacyStudioDocsService;
        this.communicationsService = communicationsService;
        this.suppliersService = suppliersService;
        this.informativeNoteService = informativeNoteService;
        this.documentArchiveExportDownloadService = documentArchiveExportDownloadService;
        this.subscriptions = [];
        this.historicalReload = false;
        this.loggedUserMaster = false;
        this.loggedUserAdminOrOperator = false;
        /** The Excel of a property being downloaded from the new archive, `null` when none (@see exportFromArchiveV3) */
        this.exportingTarget = null;
        /** The download of that Excel, cancelled when the page closes */
        this.exportSubscription = null;
        this.lang = this.translateService.currentLang || this.translateService.defaultLang;
        this.company = new Company();
        this.availableSubUsers = [];
        this.detailCardIsLoading = true;
        this.deleteCardIsLoading = true;
        this.subUsersCardIsLoading = true;
        this.subPropertiesCardIsLoading = true;
        this.suppliersCardIsLoading = true;
        this.phonebookCardIsLoading = true;
        this.companyId = this.activatedRoute.snapshot.params.companyId;
        // BEGIN - Add company form
        // BEGIN - ngx-formly
        this.form = new FormGroup({});
        this.formSC = new FormGroup({});
        this.model = {};
        this.modelSC = {};
        this.modelSCInit = {};
        this.anagraphicFields = [];
        this.contractFields = [];
        this.options = {};
        // END - ngx-formly
        this.formBtnOptions = {
            active: false,
            text: this.translateService.instant("common.cta.save"),
            spinnerSize: 19,
            raised: true,
            stroked: true,
            flat: false,
            fab: false,
            buttonColor: "primary",
            spinnerColor: "primary",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            buttonIcon: {
                fontSet: "fal",
                fontIcon: "fa-save",
                inline: true,
            },
        };
        this.priceFields = {};
        // END - Add company form
        // BEGIN - Delete company
        this.deleteBtnOptions = {
            active: false,
            text: this.translateService.instant("common.cta.deactivate"),
            spinnerSize: 19,
            raised: true,
            stroked: true,
            flat: false,
            fab: false,
            buttonColor: "warn",
            spinnerColor: "warn",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            buttonIcon: {
                fontSet: "fal",
                fontIcon: "fa-times",
                inline: true,
            },
        };
        this.reactivateBtnOptions = {
            active: false,
            text: this.translateService.instant("common.cta.activate"),
            spinnerSize: 19,
            raised: true,
            stroked: true,
            flat: false,
            fab: false,
            buttonColor: "primary",
            spinnerColor: "primary",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            buttonIcon: {
                fontSet: "fal",
                fontIcon: "fa-check",
                inline: true,
            },
        };
        // END - Delete company
        // BEGIN - SubUsers table properties
        this.subUsersDisplayedColumns = [
            "index",
            "role",
            "firstName",
            "lastName",
            "email",
            "createdAt",
            "actions",
        ];
        // END - SubUsers table properties
        // BEGIN - SubProperties table properties
        this.subPropertiesDisplayedColumns = [
            "index",
            "hasNotes",
            "name",
            "masterCode",
            "address",
            "addressCity",
            "admin",
            "status",
            "subscriptionEndDate",
            "totalDocsInserted",
            "structureOpen",
        ];
        this.adminFilter = new FormControl("");
        this.ownerFilter = new FormControl("");
        this.typeFilter = new FormControl("");
        this.nameFilter = new FormControl("");
        this.masterCodeFilter = new FormControl("");
        this.addressFilter = new FormControl("");
        this.addressCityFilter = new FormControl("");
        this.subscriptionEndDateFilter = new FormControl("");
        this.totalDocsInsertedFilter = new FormControl("");
        this.propertiesStatusFilter = new FormControl("");
        this.adminIsLoading = false;
        this.ownerIsLoading = false;
        this.availableAdmins = [];
        this.availableOwners = [];
        this.availableTypes = [];
        // END - SubProperties table properties
        this.selectedSuppliers = [];
        // BEGIN - Table suppliers
        this.suppliersDisplayedColumns = ['index', 'checkbox', 'companyName', 'status', 'employees', 'startCompanyDate', 'registration', 'durc', 'creditLicence', 'credits', 'qualifications', 'register', 'invitationDate', 'actions']; // 'email', 'pec', 
        this.companyNameFilter = new FormControl('');
        this.qualificationsFilter = new FormControl('');
        this.startCompanyDateFilter = new FormControl('');
        this.registrationFilter = new FormControl('');
        this.durcFilter = new FormControl('');
        this.creditLicenceFilter = new FormControl('');
        this.creditsFilter = new FormControl('');
        this.statusSuppliersFilter = new FormControl('');
        this.employeesFilter = new FormControl('');
        this.registerFilter = new FormControl('');
        this.invitationDateFilter = new FormControl('');
        // END - Table suppliers
        this.statusOptions = [
            { value: 'notDefined', label: this.translateService.instant('suppliers.status.notDefined') },
            { value: 'active', label: this.translateService.instant('suppliers.status.active') },
            { value: 'registered', label: this.translateService.instant('suppliers.status.registered') },
            { value: 'inactive', label: this.translateService.instant('suppliers.status.inactive') },
            { value: 'suspended', label: this.translateService.instant('suppliers.status.suspended') },
            { value: 'inRegistration', label: this.translateService.instant('suppliers.status.inRagistration') },
            { value: 'ceased', label: this.translateService.instant('suppliers.status.ceased') },
            { value: 'notRegistered', label: this.translateService.instant('suppliers.status.notRegistered') },
        ];
        // BEGIN - phonebook table properties
        this.phonebookDisplayedColumns = [
            "index",
            "firstName",
            "lastName",
            "email",
            "createdAt",
            "actions",
        ];
        // END - phonebook table properties
        // START - Access List Table properties
        this.displayedColumnsAccessList = ['user', 'email', 'company', 'type', 'role', 'createdAt'];
        // @ViewChild('input', { static: true }) input: ElementRef;
        this.userFilter = new FormControl('');
        this.typeAccessFilter = new FormControl('');
        this.emailFilter = new FormControl('');
        this.companyFilter = new FormControl('');
        this.ipAddressFilter = new FormControl('');
        this.roleFilter = new FormControl('');
        this.createdAtAccessFilter = new FormControl('');
        this.accessListCardIsLoading = true;
        this.filterRoleTypes = [];
        // END - Access List Table properties
        // BEGIN - balances table properties
        this.balancesDisplayedColumns = [
            "index",
            "property",
            "number",
            "status",
            "total",
            "discount",
            "createdAt",
            "actions",
        ];
        this.numberFilter = new FormControl("");
        this.propertyFilter = new FormControl("");
        this.statusFilter = new FormControl("");
        this.totalFilter = new FormControl("");
        this.discountFilter = new FormControl("");
        this.createdAtFilter = new FormControl("");
        this.balancesCardIsLoading = true;
        this.prices = [];
        this.isInit = true;
        this.enableEdit = false;
        this.propertyIsLoading = true;
        this.availableProperties = [];
        this.archediSubscriptions = [];
        this.previousSubscription = {};
        // List of codes
        this.firstScanCodes = this.configService.availableScanCodes;
        this.firstFixedPackCodes = this.configService.availableFixedPackageCodes;
        this.scanInsNextCodesForfait = this.configService.availableInsCodesNextForfait;
        this.scanNextCodesForfait = this.configService.availableScanCodesNextForfait;
        // disclaimer
        this.disclaimer = false;
        this.disclaimerAt = '';
        this.disclaimerUser = '';
        // BEGIN - Table safetyVideoSuppliers
        this.safetyVideoSuppliersDisplayedColumns = [
            "index",
            "firstName",
            "lastName",
            "companyName",
            "createdAt",
        ];
        this.safetyVideoSuppliersFirstNameFilter = new FormControl("");
        this.safetyVideoSuppliersLastNameFilter = new FormControl("");
        this.safetyVideoSuppliersCompanyNameFilter = new FormControl("");
        this.safetyVideoSuppliersCreatedAtFilter = new FormControl("");
        this.safetyVideoSuppliersCardIsLoading = true;
        // BEGIN - Table safetyVideoResidents
        this.safetyVideoResidentsDisplayedColumns = [
            "index",
            "firstName",
            "lastName",
            "companyName",
            "createdAt",
        ];
        this.safetyVideoResidentsFirstNameFilter = new FormControl("");
        this.safetyVideoResidentsLastNameFilter = new FormControl("");
        this.safetyVideoResidentsCompanyNameFilter = new FormControl("");
        this.safetyVideoResidentsCreatedAtFilter = new FormControl("");
        this.safetyVideoResidentsCardIsLoading = true;
        // BEGIN - Table generic docs
        this.genericDocsDisplayedColumns = ['index', 'checkbox', 'name', 'description', 'createdAt', 'actions'];
        // @ViewChild('genericDocsInput', { static: false }) genericDocsInput: ElementRef;
        this.genericDocsNameFilter = new FormControl('');
        this.genericDocsDescriptionFilter = new FormControl('');
        this.genericDocsCreatedAtFilter = new FormControl('');
        // END - Table generic docs
        this.genericDocsCardIsLoading = true;
        this.selectedGenericDocs = [];
        // BEGIN - Table fire safety docs - default
        this.fireSafetyDocsDefaultDataSource = [
            {
                _id: "68e62051087b3792d6b89aaa",
                name: this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.name"),
                description: this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.description"),
                createdAt: new Date(this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.createdAt")),
                files: [
                    {
                        _id: "6634d6c7c3c77915076f5c8c",
                        originalName: "GSA-SAG-LP-ALL.pdf",
                        filename: "GSA-SAG-LP-ALL.pdf",
                        path: "uploads/firesafetydocs/GSA-SAG-LP-ALL.pdf",
                        mimetype: "application/pdf",
                        size: 2056192,
                        createdBy: "5ef210e3e5da4742563f4c75",
                        createdAt: "2024-05-17T12:21:25.740+00:00",
                        updatedAt: "2024-05-17T12:21:25.740+00:00"
                    }
                ]
            }
        ];
        this.fireSafetyDocsefaultDisplayedColumns = ['index', 'name', 'description', 'createdAt', 'actions'];
        // END - Table fire safety docs - default
        // BEGIN - Table fire safety docs
        this.fireSafetyDocsDisplayedColumns = ['index', 'checkbox', 'name', 'description', 'createdAt', 'actions'];
        // @ViewChild('fireSafetyDocsInput', { static: false }) fireSafetyDocsInput: ElementRef;
        this.fireSafetyDocsNameFilter = new FormControl('');
        this.fireSafetyDocsDescriptionFilter = new FormControl('');
        this.fireSafetyDocsCreatedAtFilter = new FormControl('');
        // END - Table fire safety docs
        // BEGIN - Table invitation
        this.invitationDisplayedColumns = [
            "index",
            "companyName",
            "vatNumber",
            "email",
            "pec",
            "register"
        ];
        this.invitationSupplierFilter = new FormControl("");
        this.invitationVatNumberFilter = new FormControl("");
        this.invitationEmailFilter = new FormControl("");
        this.invitationPecFilter = new FormControl("");
        this.invitationRegisterFilter = new FormControl("");
        this.invitationCardIsLoading = true;
        // END - Table invitation
        this.fireSafetyDocsCardIsLoading = true;
        this.selectedFireSafetyDocs = [];
        // BEGIN - Table privacy docs
        this.privacyDocsDisplayedColumns = ['index', 'type', 'description', 'createdAt', 'actions'];
        // END - Table "Nota informativa" docs
        this.privacyDocsCardIsLoading = true;
        this.privacyDocsSendPecDisabled = false;
        // BEGIN - Table privacy studio docs
        this.privacyStudioDocsDisplayedColumns = ['index', 'checkbox', 'type', 'description', 'createdAt', 'actions'];
        this.privacyStudioDocsTypeFilter = new FormControl('');
        this.privacyStudioDocsDescriptionFilter = new FormControl('');
        this.privacyStudioDocsCreatedAtFilter = new FormControl('');
        this.selectedPrivacyStudioDocs = [];
        // END - Table privacy studio docs
        this.nfcAccess = true;
        this.suppliers = true;
        this.disableSendEmail = false;
        this.showMasterCode = false;
        this.hiddenCardSuppliers = false;
        this.hiddenCardInvitation = false;
        this.hiddenCardPrivacy = true;
        this.privacySagActive = false;
        this.privacyActive = false;
        // "Nota informativa": condo-owner privacy access key without Privacy SAG
        this.informativeNoteActive = false;
        // Saved state of each service, shown as a badge to Admin / Operator (@see setActiveServices)
        this.activeServices = {
            suppliers: false,
            safetyVideoSuppliers: false,
            safetyVideoResidents: false,
            fireSafety: false,
            informativeNote: false,
            privacy: false,
        };
        this.informativeNoteDocIsLoading = false;
    }
    /**
     * Init components
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.ngOnInit = function () {
        var _this = this;
        // Set page title
        this.themeService.setPageTitle(this.translateService.instant("companies.detail.pageTitle"));
        // Get logged user data
        this.subscriptions.push(this.authService.loggedUser$.subscribe(function (res) {
            _this.loggedUser = res;
            _this.currentRole = _this.loggedUser.userRoles.find(function (obj) {
                return (obj._id.toString() ===
                    _this.authService.loggedUserRequestedRole$.getValue().toString());
            });
            if (_this.currentRole.role.type === "master" ||
                _this.currentRole.role.type === "collaborator") {
                _this.loggedUserMaster = true;
            }
            if (_this.currentRole.role.type === "admin" ||
                _this.currentRole.role.type === "operator") {
                _this.loggedUserAdminOrOperator = true;
            }
        }));
        this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(function (res) { return (_this.loggedUserPermissions = res); }));
        // Get current lang
        this.lang =
            this.cookieService.get("lang") || this.configService.defaultLang;
        // Init dataSources
        this.subUsersDataSource = new SubUsersDataSource(this.companiesService, this.helperService);
        this.subPropertiesDataSource = new SubPropertiesDataSource(this.companiesService, this.helperService);
        this.phonebookDataSource = new PhonebookDataSource(this.companiesService, this.helperService);
        this.balancesDataSource = new BalancesDataSource(this.balancesService, this.helperService);
        this.accessListDataSource = new AccessLogsDataSource(this.accessLogsService, this.helperService);
        this.filterRoleTypes = this.rolesService.roleTypes;
        this.roleFilter.reset();
        // Se default start and end date
        this.defaultStartDate = moment().toISOString();
        this.defaultEndDate = moment().add(1, "years").subtract(1, 'days').toISOString();
        this.getPrices();
        this.safetyVideoSuppliersDataSource = new VideoViewDataSource(this.videoViewService, this.helperService);
        this.safetyVideoResidentsDataSource = new VideoViewDataSource(this.videoViewService, this.helperService);
        this.genericDocsDataSource = new GenericDocsDataSource(this.genericDocsService, this.helperService);
        this.fireSafetyDocsDataSource = new FireSafetyDocsDataSource(this.fireSafetyDocsService, this.helperService);
        this.privacyDocsDataSource = new PrivacyDocsDataSource(this.privacyDocsService, this.helperService);
        this.informativeNoteDocsDataSource = new PrivacyDocsDataSource(this.privacyDocsService, this.helperService);
        this.privacyStudioDocsDataSource = new PrivacyStudioDocsDataSource(this.privacyStudioDocsService, this.helperService);
        this.suppliersDataSource = new SuppliersNewDataSource(this.suppliersService, this.helperService, false, this.translateService);
        this.invitationDataSource = new SuppliersInvitationDataSource(this.suppliersService, this.helperService);
    };
    /**
     * After view init logic
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        // Get requested company data
        this.subscriptions.push(this.companiesService.get(this.companyId).subscribe(function (res) {
            if (res.status) {
                // Set company data
                _this.company.setData(res.data);
                _this.companyData = res.data;
                // The "Nota informativa" puts the condo-owner access key in the Privacy section
                _this.hiddenCardPrivacy = !(res.data.subscription &&
                    (res.data.subscription.privacy || res.data.subscription.privacySag || res.data.subscription.informativeNote));
                _this.privacySagActive = !!(res.data.subscription && res.data.subscription.privacySag);
                _this.privacyActive = !!(res.data.subscription && res.data.subscription.privacy);
                _this.informativeNoteActive = !!(res.data.subscription && res.data.subscription.informativeNote);
                _this.setActiveServices(res.data.subscription);
                for (var prop in res.data.subscription) {
                    _this.previousSubscription[prop] =
                        res.data.subscription[prop];
                    if (prop === 'nfcAccess') {
                        _this.nfcAccess = res.data.subscription[prop];
                        if (!_this.nfcAccess) {
                            var index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'checkbox'; });
                            _this.suppliersDisplayedColumns.splice(index, 1);
                            index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'register'; });
                            _this.suppliersDisplayedColumns.splice(index, 1);
                            index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'invitationDate'; });
                            _this.suppliersDisplayedColumns.splice(index, 1);
                        }
                    }
                    if (prop === 'suppliers') {
                        _this.suppliers = res.data.subscription[prop];
                    }
                }
                if (_this.suppliers) {
                    _this.hiddenCardInvitation = true;
                    _this.hiddenCardSuppliers = false;
                }
                else if (_this.nfcAccess) {
                    _this.hiddenCardInvitation = false;
                    _this.hiddenCardSuppliers = true;
                }
                if (res.data.disableSendEmailNfcAccess) {
                    _this.disableSendEmail = res.data.disableSendEmailNfcAccess;
                }
                if (res.data.showMasterCode) {
                    _this.showMasterCode = res.data.showMasterCode;
                }
                if (_this.loggedUserMaster && !_this.showMasterCode) {
                    var masterCodeIndex = _this.subPropertiesDisplayedColumns.findIndex(function (el) { return el === 'masterCode'; });
                    if (masterCodeIndex > -1) {
                        _this.subPropertiesDisplayedColumns.splice(masterCodeIndex, 1);
                    }
                }
                // Disclaimer
                if (res.data.disclaimer &&
                    res.data.disclaimer.check1 &&
                    res.data.disclaimer.check2 &&
                    res.data.disclaimer.check3 &&
                    res.data.disclaimer.check4) {
                    _this.disclaimer = true;
                    _this.disclaimerAt = res.data.disclaimer.dateCheck1;
                    _this.subscriptions.push(_this.usersService.get(res.data.disclaimer.check1UpdatedBy).subscribe(function (res) {
                        if (res.status) {
                            _this.disclaimerUser = res.data.firstName + " " + res.data.lastName;
                        }
                    }));
                }
                _this.removeIdsFromPrices(_this.company.get("subscription.prices"));
                // Save subscription key value for future uses
                // this.previousKey = res.data.subscription && res.data.subscription.key ? res.data.subscription.key : undefined;
                // Stop loading in delete form
                _this.deleteCardIsLoading = false;
                // Init edit form
                setTimeout(function () {
                    _this.initForm();
                }, 500);
                // Make detail form card visible by disabling his card-spinner
                _this.detailCardIsLoading = false;
                // If there are no subUsers, let's create an empty array
                if (!_this.company.get("subUsers")) {
                    _this.company.set("subUsers", []);
                }
                // Init subUsers
                _this.initSubUsersDataTable();
                // Stop loading subUsers card
                _this.subUsersCardIsLoading = false;
                // If there are no subProperties, let's create an empty array
                if (!_this.company.get("subProperties")) {
                    _this.company.set("subProperties", []);
                }
                // Init subProperties
                _this.initSubPropertiesDataTable();
                // Stop loading subProperties card
                _this.subPropertiesCardIsLoading = false;
                // Init suppliers
                _this.initSuppliersDataTable();
                _this.suppliersCardIsLoading = false;
                // Init phonebook
                _this.initPhonebookDataTable();
                // Stop loading phonebook card
                _this.phonebookCardIsLoading = false;
                // Init AccessList
                _this.initAccessListDataTable();
                // Stop loading phonebook card
                _this.accessListCardIsLoading = false;
                // Subscribe to listsService and update this list settings
                if (_this.loggedUserPermissions.includes("canReadBalancesList")) {
                    _this.subscribeToListsSettings("balances");
                    _this.initBalancesDataTable();
                }
                _this.balancesCardIsLoading = false;
                _this.getAvailableProperties();
                _this.initSafetyVideoSuppliersDataTable();
                _this.safetyVideoSuppliersCardIsLoading = false;
                var section = "safetyVideoSuppliers";
                var host = _this.configService.frontEndHost;
                _this.safetyVideoSuppliersQrcodeData = host + "/qrcode/" + _this.companyId + "/" + section;
                _this.initSafetyVideoResidentsDataTable();
                _this.safetyVideoResidentsCardIsLoading = false;
                section = "safetyVideoResidents";
                host = _this.configService.frontEndHost;
                _this.safetyVideoResidentsQrcodeData = host + "/qrcode/" + _this.companyId + "/" + section;
                _this.initGenericDocsDataTable();
                _this.genericDocsCardIsLoading = false;
                _this.initFireSafetyDocsDataTable();
                _this.fireSafetyDocsCardIsLoading = false;
                _this.initPrivacyDocsDataTable();
                _this.initInformativeNoteDocsDataTable();
                _this.privacyDocsCardIsLoading = false;
                _this.initPrivacyStudioDocsDataTable();
                // Init invitationsuppliers
                _this.initInvitationDataTable();
                _this.invitationCardIsLoading = false;
                _this.isInit = false;
                return;
            }
            // Something bad happened
            // redirect user to home
            _this.router.navigate([_this.lang, "app", "companies", "list"]);
        }, function (err) {
            // if we came here users with given userId do not exist
            _this.router.navigate([_this.lang, "errors", "404"]);
        }));
    };
    /**
     * Handle component destroy
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.ngOnDestroy = function () {
        // Unsubscribe from all subscriptions
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
        if (this.exportSubscription) {
            this.exportSubscription.unsubscribe();
        }
        // Push current list settings to the listsService
        if (this.loggedUserPermissions.includes("canReadBalancesList")) {
            this.listsService.setListSetting("balances", this.balancesPaginator.pageIndex.valueOf(), this.balancesPaginator.pageSize.valueOf(), {
                general: this.balancesInput.nativeElement.value,
                number: this.numberFilter.value,
                property: this.propertyFilter.value,
                status: this.statusFilter.value,
                total: this.totalFilter.value,
                discount: this.discountFilter.value,
                createdAt: this.createdAtFilter.value,
            });
        }
        // Push current list settings to the listsService
        this.listsService.setListSetting("properties", this.subPropertiesPaginator.pageIndex.valueOf(), this.subPropertiesPaginator.pageSize.valueOf(), {
            general: this.subPropertiesInput.nativeElement.value,
            admin: this.adminFilter.value,
            owner: this.ownerFilter.value,
            type: this.typeFilter.value,
            name: this.nameFilter.value,
            masterCode: this.masterCodeFilter.value,
            address: this.addressFilter.value,
            addressCity: this.addressCityFilter.value,
            subscriptionEndDate: this.subscriptionEndDateFilter.value
                ? moment(this.subscriptionEndDateFilter.value).toISOString()
                : null,
            totalDocsInserted: this.totalDocsInsertedFilter.value,
            status: this.propertiesStatusFilter.value,
        });
        // Push current list settings to the listsService
        this.listsService.setListSetting('accessLogs', this.accessListPaginator.pageIndex.valueOf(), this.accessListPaginator.pageSize.valueOf(), {
            general: '',
            user: this.userFilter.value,
            type: this.typeAccessFilter.value,
            email: this.emailFilter.value,
            company: this.companyFilter.value,
            ipAddress: this.ipAddressFilter.value,
            role: this.roleFilter.value,
            createdAt: this.createdAtAccessFilter.value,
        });
        if (this.safetyVideoSuppliersPaginator) {
            // Push current list settings to the listsService
            this.listsService.setListSetting("safetyVideoSuppliers", this.safetyVideoSuppliersPaginator.pageIndex.valueOf(), this.safetyVideoSuppliersPaginator.pageSize.valueOf(), {
                firstNameFilter: this.safetyVideoSuppliersFirstNameFilter.value,
                lastNameFilter: this.safetyVideoSuppliersLastNameFilter.value,
                companyNameFilter: this.safetyVideoSuppliersCompanyNameFilter.value,
                createdAtFilter: this.safetyVideoSuppliersCreatedAtFilter.value,
            });
        }
        if (this.safetyVideoResidentsPaginator) {
            // Push current list settings to the listsService
            this.listsService.setListSetting("safetyVideoResidents", this.safetyVideoResidentsPaginator.pageIndex.valueOf(), this.safetyVideoResidentsPaginator.pageSize.valueOf(), {
                firstNameFilter: this.safetyVideoResidentsFirstNameFilter.value,
                lastNameFilter: this.safetyVideoResidentsLastNameFilter.value,
                companyNameFilter: this.safetyVideoResidentsCompanyNameFilter.value,
                createdAtFilter: this.safetyVideoResidentsCreatedAtFilter.value,
            });
        }
        if (this.genericDocsPaginator) {
            // Push current list settings to the listsService
            this.listsService.setListSetting('genericDocs', this.genericDocsPaginator.pageIndex.valueOf(), this.genericDocsPaginator.pageSize.valueOf(), {
                name: this.genericDocsNameFilter.value,
                description: this.genericDocsDescriptionFilter.value,
                createdAt: this.genericDocsCreatedAtFilter.value,
            });
        }
        if (this.fireSafetyDocsPaginator) {
            // Push current list settings to the listsService
            this.listsService.setListSetting('fireSafetyDocs', this.fireSafetyDocsPaginator.pageIndex.valueOf(), this.fireSafetyDocsPaginator.pageSize.valueOf(), {
                name: this.fireSafetyDocsNameFilter.value,
                description: this.fireSafetyDocsDescriptionFilter.value,
                createdAt: this.fireSafetyDocsCreatedAtFilter.value,
            });
        }
        if (this.invitationPaginator) {
            // Push current list settings to the listsService
            this.listsService.setListSetting('invitation', this.invitationPaginator.pageIndex.valueOf(), this.invitationPaginator.pageSize.valueOf(), {
                name: this.fireSafetyDocsNameFilter.value,
                description: this.fireSafetyDocsDescriptionFilter.value,
                createdAt: this.fireSafetyDocsCreatedAtFilter.value,
                companyName: this.invitationSupplierFilter.value,
                pec: this.invitationPecFilter.value,
                email: this.invitationEmailFilter.value,
                vatNumber: this.invitationVatNumberFilter.value,
                register: this.invitationRegisterFilter.value,
            });
        }
    };
    /**
     * Get prices from backend
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.getPrices = function () {
        var _this = this;
        this.subscriptions.push(this.pricesService.getList(0, 15).subscribe(function (res) {
            var _a;
            if (res.status) {
                (_a = _this.prices).push.apply(_a, tslib_1.__spread(res.data.items));
                _this.getSubscriptions();
            }
        }));
    };
    /**
     * Load requested subscription data
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.getSubscriptions = function () {
        var _this = this;
        this.subscriptionsService.getList().subscribe(function (res) {
            if (res.status) {
                // this.archediSubscriptions = res.data.items;
                var subscriptions = res.data.items.map(function (item) {
                    return {
                        value: item,
                        label: item.name,
                    };
                });
                _this.availableSubscriptions = subscriptions.filter(function (element) {
                    return (element.value.key !== "QRANTINCENDIO");
                });
                _this.availableSubscriptionsKeys = _this.availableSubscriptions.map(function (element) {
                    return {
                        value: element.value.key,
                        label: element.value.name,
                    };
                });
                // Create an empty value
                var noSubscription = {
                    value: "noSubscription",
                    label: _this.translateService.instant("subscriptions.fields.noSubscription"),
                };
                _this.availableSubscriptionsKeys.push(noSubscription);
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Init form
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initForm = function () {
        var e_1, _a, e_2, _b, e_3, _c;
        var _this = this;
        var companySubscription = this.company.get('subscription');
        if (companySubscription.prices &&
            Array.isArray(companySubscription.prices) &&
            companySubscription.prices.length === 0 &&
            this.prices &&
            Array.isArray(this.prices)) {
            try {
                for (var _d = tslib_1.__values(this.prices), _e = _d.next(); !_e.done; _e = _d.next()) {
                    var price = _e.value;
                    companySubscription.prices.push({
                        key: price.key,
                        price: price.fee,
                        discount: 0
                    });
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        if (typeof companySubscription.privacy === "undefined") {
            companySubscription.privacy = false;
        }
        if (typeof companySubscription.privacySag === "undefined") {
            companySubscription.privacySag = false;
        }
        // NFC TAG fee was introduced after NFC access: companies saved before it have no
        // value, which leaves the field required-but-empty when NFC access is active
        if (companySubscription.nfcAccessTagFee === undefined ||
            companySubscription.nfcAccessTagFee === null) {
            companySubscription.nfcAccessTagFee = 0;
        }
        try {
            // Additional services: companies saved before they were split have neither the
            // flags nor the fees, so they inherit the legacy "additionalServices" flag and its
            // single fee; saving the contract stores them explicitly (lazy migration)
            for (var ADDITIONAL_SERVICE_FLAGS_1 = tslib_1.__values(ADDITIONAL_SERVICE_FLAGS), ADDITIONAL_SERVICE_FLAGS_1_1 = ADDITIONAL_SERVICE_FLAGS_1.next(); !ADDITIONAL_SERVICE_FLAGS_1_1.done; ADDITIONAL_SERVICE_FLAGS_1_1 = ADDITIONAL_SERVICE_FLAGS_1.next()) {
                var service = ADDITIONAL_SERVICE_FLAGS_1_1.value;
                var feeKey = service + "Fee";
                companySubscription[feeKey] = additionalServiceFee(companySubscription, service);
                companySubscription[service] = isAdditionalServiceActive(companySubscription, service);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (ADDITIONAL_SERVICE_FLAGS_1_1 && !ADDITIONAL_SERVICE_FLAGS_1_1.done && (_b = ADDITIONAL_SERVICE_FLAGS_1.return)) _b.call(ADDITIONAL_SERVICE_FLAGS_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        // Set model
        this.model = {
            name: this.company.get('name'),
            types: this.company.get('types'),
            vatNumber: this.company.get('vatNumber'),
            taxCode: this.company.get('taxCode'),
            email: this.company.get('email'),
            pec: this.company.get('pec'),
            sdiCode: this.company.get('sdiCode'),
            legalAddress: this.company.get('legalAddress'),
            operativeAddress: this.company.get('operativeAddress'),
            phone: this.company.get('phone'),
            webSiteUrl: this.company.get('webSiteUrl'),
            legalName: this.company.get('legalName'),
            legalLastName: this.company.get('legalLastName'),
            legalTaxCode: this.company.get('legalTaxCode'),
            legalEmail: this.company.get('legalEmail'),
            legalResidentialAddress: this.company.get('legalResidentialAddress'),
            subscription: companySubscription,
        };
        this.modelSC = {
            subscription: companySubscription,
        };
        // For price list sorting
        var index = 0;
        var categoriesOptions = [];
        var rawCategories = this.translateService.instant("companies.categories");
        try {
            for (var _f = tslib_1.__values(Object.keys(rawCategories)), _g = _f.next(); !_g.done; _g = _f.next()) {
                var trans = _g.value;
                categoriesOptions.push({
                    label: rawCategories[trans],
                    value: trans,
                });
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_c = _f.return)) _c.call(_f);
            }
            finally { if (e_3) throw e_3.error; }
        }
        // Set fields
        this.anagraphicFields = [
            // ...this.anagraphicFields,
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12 col-md-6",
                        type: "input",
                        key: "name",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.name"),
                            placeholder: this.translateService.instant("companies.fields.name"),
                            required: true,
                            minLength: 2,
                            maxLength: 100,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "select",
                        key: "types",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.types"),
                            placeholder: this.translateService.instant("companies.fields.types"),
                            required: true,
                            multiple: true,
                            options: [
                                {
                                    value: "owner",
                                    label: this.translateService.instant("companies.types.owner"),
                                },
                                {
                                    value: "manager",
                                    label: this.translateService.instant("companies.types.manager"),
                                },
                            ],
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "input",
                        key: "vatNumber",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.vatNumber"),
                            placeholder: this.translateService.instant("companies.fields.vatNumber"),
                            required: false,
                            minLength: 0,
                            maxLength: 20,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "input",
                        key: "taxCode",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.taxCode"),
                            placeholder: this.translateService.instant("companies.fields.taxCode"),
                            required: false,
                            minLength: 0,
                            maxLength: 20,
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "email",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.email"),
                            placeholder: this.translateService.instant("companies.fields.email"),
                            required: true,
                            minLength: 5,
                            maxLength: 320,
                            type: "email",
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "pec",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.pec"),
                            placeholder: this.translateService.instant("companies.fields.pec"),
                            required: false,
                            minLength: 5,
                            maxLength: 320,
                            type: "email",
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "sdiCode",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.sdiCode"),
                            placeholder: this.translateService.instant("companies.fields.sdiCode"),
                            required: false,
                            minLength: 6,
                            maxLength: 7,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "address",
                        key: "legalAddress",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.legalAddress"),
                            placeholder: this.translateService.instant("companies.fields.legalAddress"),
                            required: true,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "address",
                        key: "operativeAddress",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.operativeAddress"),
                            placeholder: this.translateService.instant("companies.fields.operativeAddress"),
                            required: false,
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "phone",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.phone"),
                            placeholder: this.translateService.instant("companies.fields.phone"),
                            required: true,
                            minLength: 1,
                            maxLength: 15,
                            pattern: this.configService.phonePattern,
                        },
                    },
                    {
                        className: "col-12 col-md-8",
                        type: "input",
                        key: "webSiteUrl",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.webSiteUrl"),
                            placeholder: this.translateService.instant("companies.fields.webSiteUrl"),
                            required: false,
                            minLength: 1,
                            maxLength: 100,
                            pattern: this.configService.webSitePattern,
                        },
                    },
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.legalRepresentative") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "legalName",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.legalName"),
                            placeholder: this.translateService.instant("companies.fields.legalName"),
                            required: false,
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "legalLastName",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.legalLastName"),
                            placeholder: this.translateService.instant("companies.fields.legalLastName"),
                            required: false,
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "legalTaxCode",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.taxCode"),
                            placeholder: this.translateService.instant("companies.fields.taxCode"),
                            required: false,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "address",
                        key: "legalResidentialAddress",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.legalResidentialAddress"),
                            placeholder: this.translateService.instant("companies.fields.legalResidentialAddress"),
                            required: false,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "input",
                        key: "legalEmail",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.legalEmail"),
                            placeholder: this.translateService.instant("companies.fields.legalEmail"),
                            required: false,
                            minLength: 5,
                            maxLength: 320,
                            type: "email",
                        },
                    },
                ],
            },
        ];
        this.contractFields = [
            // 1. PLATFORM SUBSCRIPTION
            {
                fieldGroupClassName: "row",
                hooks: {
                    onInit: function (fieldConfig) {
                        _this.updateSubscriptionCodes();
                    },
                },
                fieldGroup: [
                    {
                        className: "col-12 boxForm",
                        template: "<div><strong>" +
                            this.translateService.instant("companies.fields.subscription.subscription") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "checkbox",
                        key: "subscription.documentUpload",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.documentUpload.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.documentUpload.title"),
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "checkbox",
                        key: "subscription.noticeTacitRenewalContract",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.noticeTacitRenewalContract.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.noticeTacitRenewalContract.title"),
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-4",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "select",
                        key: "subscription.invoiceHolder",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.invoiceHolder.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.invoiceHolder.title"),
                            required: true,
                            multiple: false,
                            options: [
                                {
                                    value: "property",
                                    label: this.translateService.instant("companies.fields.subscription.invoiceHolder.property"),
                                },
                                {
                                    value: "admin",
                                    label: this.translateService.instant("companies.fields.subscription.invoiceHolder.admin"),
                                },
                                {
                                    value: "owner",
                                    label: this.translateService.instant("companies.fields.subscription.invoiceHolder.owner"),
                                },
                            ],
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.documentUpload",
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit || !_this.modelSC.subscription.documentUpload;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-8",
                    },
                    {
                        className: "col-4",
                        type: "select",
                        key: "subscription.key",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.subscriptionType"),
                            placeholder: this.translateService.instant("companies.fields.subscription.subscriptionType"),
                            multiple: false,
                            options: this.availableSubscriptionsKeys,
                            required: true,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit || !_this.modelSC.subscription.documentUpload;
                            },
                        },
                    },
                    {
                        className: "col-4",
                        type: "input",
                        key: "subscription.fee",
                        templateOptions: {
                            label: this.translateService.instant("subscriptions.fields.fee"),
                            placeholder: this.translateService.instant("subscriptions.fields.fee"),
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 10000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit || !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY")));
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-4",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "subscription.availableSpace",
                        templateOptions: {
                            label: this.translateService.instant("subscriptions.fields.availableSpace"),
                            placeholder: this.translateService.instant("subscriptions.fields.availableSpace"),
                            type: "number",
                            min: 0,
                            max: 1000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit || !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY")));
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "subscription.totalStorageSpaceUsed",
                        templateOptions: {
                            label: this.translateService.instant("subscriptions.fields.totalStorageSpaceUsed"),
                            placeholder: this.translateService.instant("subscriptions.fields.totalStorageSpaceUsed"),
                            type: "number",
                            step: 0.001,
                            min: 0,
                            max: 50000,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-0 col-md-4",
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                ],
            },
            // 2. STRUCTURE SUBSCRIPTION
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.structure.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.structure",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.structure.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.structure.title"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY' && model.subscription.key !== 'FREE' && model.subscription.key !== 'FREEBASIC'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY" ||
                                            _this.modelSC.subscription.key === "FREE" ||
                                            _this.modelSC.subscription.key === "FREEBASIC")));
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.structurePrice",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.structurePrice"),
                            placeholder: this.translateService.instant("companies.fields.subscription.structurePrice"),
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 10000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.structure",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY" ||
                                            _this.modelSC.subscription.key === "FREE" ||
                                            _this.modelSC.subscription.key === "FREEBASIC" ||
                                            !_this.modelSC.subscription.structure)));
                            },
                        },
                    },
                ],
            },
            // 3. FIRST BALANCE
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.firstBalance.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.firstPriceList",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.firstPriceList.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.firstPriceList.title"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY")));
                            },
                        },
                    },
                    {
                        className: "col-1",
                    },
                ],
            },
            // 4. SUBSEQUENT INSERTIONS
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.subsequentInsertions.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.scanInsNext",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.scanInsNext.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.scanInsNext.title"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY" ||
                                            _this.modelSC.subscription.flatRate)));
                            },
                        },
                    },
                    {
                        className: "col-10",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.flatRate",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.title"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY" ||
                                            _this.modelSC.subscription.scanInsNext)));
                            },
                        },
                    },
                    {
                        className: "col-2",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalDoc",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalDoc"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalDoc"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalPdf",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalPdf"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalPdf"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                ],
            },
            // 5. SUBSEQUENT SCANS
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.subsequentScans.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.scanNext",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.scanNext.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.scanNext.title"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY" ||
                                            _this.modelSC.subscription.flatRateScan)));
                            },
                        },
                    },
                    {
                        className: "col-10",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.flatRateScan",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRateScan.title"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRateScan.title"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.documentUpload ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY" ||
                                            _this.modelSC.subscription.scanNext)));
                            },
                        },
                    },
                    {
                        className: "col-10",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalA4",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalA4"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalA4"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalA3",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalA3"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalA3"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalA2",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalA2"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalA2"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-1",
                    },
                    {
                        className: "col-2",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalA1",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalA1"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalA1"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalA0",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalA0"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalA0"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.totalOutFormat",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.flatRate.totalOutFormat"),
                            placeholder: this.translateService.instant("companies.fields.subscription.flatRate.totalOutFormat"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            disabled: true,
                        },
                    },
                    {
                        className: "col-1",
                    },
                ],
            },
            // 6. SUBSCRIPTION OPTIONS
            {
                fieldGroupClassName: "row",
                fieldGroup: tslib_1.__spread([
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.optionsServices.sectionTitle") +
                            "</strong></div><br />",
                    },
                    // Each additional service has its own flag and fee (not tied to documentUpload)
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div><strong>" +
                            this.translateService.instant("companies.fields.subscription.optionsServices.safetyVideo") +
                            "</strong></div>",
                    }
                ], this.additionalServiceFields("safetyVideoResidents", "companies.fields.subscription.optionsServices.safetyVideoResidents"), this.additionalServiceFields("safetyVideoSuppliers", "companies.fields.subscription.optionsServices.safetyVideoSuppliers"), [
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div><strong>" +
                            this.translateService.instant("companies.fields.subscription.optionsServices.fireSafety") +
                            "</strong></div>",
                    }
                ], this.additionalServiceFields("fireSafety", "companies.fields.subscription.optionsServices.fireSafety", "col-md-4"), [
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div><strong>" +
                            this.translateService.instant("companies.fields.subscription.informativeNote") +
                            "</strong></div>",
                    }
                ], this.additionalServiceFields("informativeNote", "companies.fields.subscription.informativeNote"), [
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.optionsNfc.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "checkbox",
                        key: "subscription.nfcAccess",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.nfcAccess"),
                            placeholder: this.translateService.instant("companies.fields.subscription.nfcAccess"),
                            required: false,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "input",
                        key: "subscription.nfcAccessFee",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.nfcAccessFee"),
                            placeholder: this.translateService.instant("companies.fields.subscription.nfcAccessFee"),
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 10000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.nfcAccess",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.nfcAccess);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "input",
                        key: "subscription.nfcAccessTagFee",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.nfcAccessTagFee"),
                            placeholder: this.translateService.instant("companies.fields.subscription.nfcAccessTagFee"),
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 10000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.nfcAccess",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.nfcAccess);
                            },
                        },
                    },
                    {
                        className: "col-md-4",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "datepicker",
                        key: "subscription.startDateNfcAccess",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.startDateNfcAccess"),
                            placeholder: this.translateService.instant("companies.fields.subscription.startDateNfcAccess"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.nfcAccess",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.nfcAccess);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "datepicker",
                        key: "subscription.endDateNfcAccess",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.endDateNfcAccess"),
                            placeholder: this.translateService.instant("companies.fields.subscription.endDateNfcAccess"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.nfcAccess",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.nfcAccess);
                            },
                        },
                    },
                    {
                        className: "col-md-4",
                    },
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.optionsSupplierEvaluation.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "checkbox",
                        key: "subscription.suppliers",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.suppliers"),
                            placeholder: this.translateService.instant("companies.fields.subscription.suppliers"),
                            required: false,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "subscription.suppliersFee",
                        templateOptions: {
                            label: this.translateService.instant("subscriptions.fields.fee"),
                            placeholder: this.translateService.instant("subscriptions.fields.fee"),
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 10000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.suppliers",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.suppliers);
                            },
                        },
                    },
                    {
                        className: "col-md-4",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "datepicker",
                        key: "subscription.startDateSuppliers",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.startDateSuppliers"),
                            placeholder: this.translateService.instant("companies.fields.subscription.startDateSuppliers"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.suppliers",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.suppliers);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "datepicker",
                        key: "subscription.endDateSuppliers",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.endDateSuppliers"),
                            placeholder: this.translateService.instant("companies.fields.subscription.endDateSuppliers"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.suppliers",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    !_this.modelSC.subscription.suppliers);
                            },
                        },
                    },
                    {
                        className: "col-md-4",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "checkbox",
                        key: "subscription.suppliersNotify",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.suppliersNotify"),
                            placeholder: this.translateService.instant("companies.fields.subscription.suppliersNotify"),
                            required: false,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit);
                            },
                        },
                    },
                    {
                        className: "col-md-8",
                    },
                    {
                        className: "col-md-4",
                    },
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.optionsPrivacy.sectionTitle") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.privacy",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.privacy"),
                            placeholder: this.translateService.instant("companies.fields.subscription.privacy"),
                            required: false,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    _this.modelSC.subscription.privacySag);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-2",
                        type: "checkbox",
                        key: "subscription.privacySag",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.privacySag"),
                            required: false,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    _this.modelSC.subscription.privacy);
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "input",
                        key: "subscription.privacyFee",
                        templateOptions: {
                            label: this.translateService.instant("subscriptions.fields.fee"),
                            placeholder: this.translateService.instant("subscriptions.fields.fee"),
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 10000,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    (!_this.modelSC.subscription.privacy && !_this.modelSC.subscription.privacySag));
                            },
                        },
                    },
                    {
                        className: "col-md-4",
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "datepicker",
                        key: "subscription.startDatePrivacy",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.startDatePrivacy"),
                            placeholder: this.translateService.instant("companies.fields.subscription.startDatePrivacy"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    (!_this.modelSC.subscription.privacy && !_this.modelSC.subscription.privacySag));
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-4",
                        type: "datepicker",
                        key: "subscription.endDatePrivacy",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.endDatePrivacy"),
                            placeholder: this.translateService.instant("companies.fields.subscription.endDatePrivacy"),
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    (!_this.modelSC.subscription.privacy && !_this.modelSC.subscription.privacySag));
                            },
                        },
                    },
                ]),
            },
            // 7. PRICE LIST
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12",
                        template: "<hr /><div><strong>" +
                            this.translateService.instant("companies.fields.subscription.priceList.title") +
                            "</strong></div><br />",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12",
                        template: "<hr />",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12",
                        template: "<hr />",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("prices.types." + this.prices[index].name) +
                            "</div>",
                    },
                    {
                        key: "subscription.prices[" + index + "].key",
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index + "].price",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.price"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.priceService1"),
                            required: false,
                            type: "number",
                            step: 0.1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-12 col-md-3",
                        type: "input",
                        key: "subscription.prices[" + index++ + "].discount",
                        templateOptions: {
                            label: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            placeholder: this.translateService.instant("companies.fields.subscription.priceList.discount"),
                            required: false,
                            type: "number",
                            step: 1,
                            min: 0,
                            max: 100,
                        },
                        expressionProperties: {
                            "templateOptions.disabled": function () {
                                return !_this.enableEdit;
                            },
                        },
                    },
                    {
                        className: "col-0 col-md-6",
                    },
                    {
                        className: "col-12",
                        template: "<hr />",
                    },
                    {
                        className: "col-12 section-label sub-section-label",
                        template: "<div>" +
                            this.translateService.instant("companies.fields.subscription.additionalStorage.sectionTitle") +
                            "</div>",
                    },
                    {
                        className: "col-3",
                        type: "input",
                        key: "subscription.extraSpaceFee",
                        templateOptions: {
                            label: this.translateService.instant("subscriptions.fields.extraSpaceFee"),
                            placeholder: this.translateService.instant("subscriptions.fields.extraSpaceFee"),
                            type: "number",
                            min: 0,
                            max: 500,
                        },
                        expressionProperties: {
                            "templateOptions.required": "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
                            "templateOptions.disabled": function () {
                                return (!_this.enableEdit ||
                                    (_this.modelSC.subscription.key &&
                                        (_this.modelSC.subscription.key === "noSubscription" || _this.modelSC.subscription.key === "PRIVACY")));
                            },
                        },
                    },
                ],
            },
        ];
    };
    /**
     * Refresh the "service active" badges shown to Admin / Operator on each
     * section: a service is active when its contract flag is saved as selected
     *
     * @since 3.0.0
     *
     * @param subscription Saved contractual conditions of the company
     */
    CompanyDetailComponent.prototype.setActiveServices = function (subscription) {
        var saved = subscription || {};
        this.activeServices = {
            suppliers: saved.suppliers === true,
            safetyVideoSuppliers: isAdditionalServiceActive(saved, "safetyVideoSuppliers"),
            safetyVideoResidents: isAdditionalServiceActive(saved, "safetyVideoResidents"),
            fireSafety: isAdditionalServiceActive(saved, "fireSafety"),
            informativeNote: isAdditionalServiceActive(saved, "informativeNote"),
            privacy: saved.privacy === true || saved.privacySag === true,
        };
    };
    /**
     * Build the "flag + fee" row of a single additional service.
     * The fee is not shown: its control stays in the form (hidden, not required)
     * so the stored value is kept untouched on save.
     *
     * @since 3.0.0
     *
     * @param flag Subscription flag key (its fee is stored in `${flag}Fee`)
     * @param labelKey Translation key of the checkbox label
     * @param widthClass Bootstrap width of the checkbox from md up: `col-md-auto` keeps
     *   it as wide as its label, a fixed column lets a long label wrap
     * @returns Formly fields of the row (checkbox, hidden fee): consecutive services share a line
     */
    CompanyDetailComponent.prototype.additionalServiceFields = function (flag, labelKey, widthClass) {
        var _this = this;
        if (widthClass === void 0) { widthClass = "col-md-auto"; }
        return [
            {
                className: "col-12 " + widthClass,
                type: "checkbox",
                key: "subscription." + flag,
                templateOptions: {
                    label: this.translateService.instant(labelKey),
                    required: false,
                },
                expressionProperties: {
                    "templateOptions.disabled": function () { return !_this.enableEdit; },
                },
            },
            {
                className: "d-none",
                type: "input",
                key: "subscription." + flag + "Fee",
                templateOptions: {
                    label: this.translateService.instant("subscriptions.fields.fee"),
                    placeholder: this.translateService.instant("subscriptions.fields.fee"),
                    type: "number",
                    step: 0.1,
                    min: 0,
                    max: 10000,
                },
                expressionProperties: {
                    "templateOptions.disabled": function () {
                        return !_this.enableEdit || !_this.modelSC.subscription[flag];
                    },
                },
            },
        ];
    };
    /**
     * Explain why a change of a linked service flag was refused (and reverted)
     *
     * @since 3.0.0
     *
     * @param refusal Reason of the refusal
     */
    CompanyDetailComponent.prototype.onServiceLinkRefused = function (refusal) {
        this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("companies.dialog.serviceLinks.title"),
                message: this.translateService.instant("companies.dialog.serviceLinks." + refusal),
                btnCancelText: this.translateService.instant("common.cta.close"),
                btnCancelColor: "primary",
                messageOnly: true,
            },
            width: "500px",
        });
    };
    /**
     * Autofill subscription values based on subscription type changes
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.updateSubscriptionCodes = function () {
        var _a;
        var _this = this;
        var subscriptionControl = this.formSC.controls.subscription;
        // Control fields
        var keyControl = this.formSC.controls.subscription.get("key");
        var startDateControl = this.formSC.controls.subscription.get("startDate");
        var suppliersControl = this.formSC.controls.subscription.get("suppliers");
        var nfcAccessControl = this.formSC.controls.subscription.get("nfcAccess");
        var startDateNfcAccessControl = this.formSC.controls.subscription.get("startDateNfcAccess");
        var startDateSuppliersControl = this.formSC.controls.subscription.get("startDateSuppliers");
        var privacyControl = this.formSC.controls.subscription.get("privacy");
        var privacySagControl = this.formSC.controls.subscription.get("privacySag");
        var startDatePrivacyControl = this.formSC.controls.subscription.get("startDatePrivacy");
        // Value fields
        var feeField = subscriptionControl.get("fee");
        var startDateField = subscriptionControl.get("startDate");
        var endDateField = subscriptionControl.get("endDate");
        var availableSpaceField = subscriptionControl.get("availableSpace");
        var structureField = subscriptionControl.get("structure");
        var structurePriceField = subscriptionControl.get("structurePrice");
        var firstPriceListField = subscriptionControl.get("firstPriceList");
        var scanInsNextField = subscriptionControl.get("scanInsNext");
        var flatRateField = subscriptionControl.get("flatRate");
        var scanNextField = subscriptionControl.get("scanNext");
        var flatRateScanField = subscriptionControl.get("flatRateScan");
        var startDateSuppliersField = subscriptionControl.get("startDateSuppliers");
        var endDateSuppliersField = subscriptionControl.get("endDateSuppliers");
        var nfcAccessField = subscriptionControl.get("nfcAccess");
        var startDateNfcAccessField = subscriptionControl.get("startDateNfcAccess");
        var endDateNfcAccessField = subscriptionControl.get("endDateNfcAccess");
        var extraSpaceFeeField = subscriptionControl.get("extraSpaceFee");
        var startDatePrivacyField = subscriptionControl.get("startDatePrivacy");
        var endDatePrivacyField = subscriptionControl.get("endDatePrivacy");
        if (keyControl) {
            keyControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(keyControl.value))
                .subscribe(function (res) {
                if (!res) {
                    return;
                }
                var index = -1;
                switch (res) {
                    case "noSubscription":
                    case "PRIVACY":
                        feeField.setValue(0);
                        availableSpaceField.setValue(0);
                        extraSpaceFeeField.setValue(0);
                        structurePriceField.setValue(0);
                        structureField.setValue(false);
                        firstPriceListField.setValue(false);
                        scanInsNextField.setValue(false);
                        flatRateField.setValue(false);
                        scanNextField.setValue(false);
                        flatRateScanField.setValue(false);
                        nfcAccessField.setValue(false);
                        break;
                    case "FULL":
                        index = _this.archediSubscriptions.findIndex(function (el) {
                            return el.key === "FULL";
                        });
                        structureField.setValue(true);
                        firstPriceListField.setValue(true);
                        scanInsNextField.setValue(false);
                        flatRateField.setValue(true);
                        scanNextField.setValue(true);
                        flatRateScanField.setValue(false);
                        break;
                    case "FULLBASIC":
                        index = _this.archediSubscriptions.findIndex(function (el) {
                            return el.key === "FULLBASIC";
                        });
                        structureField.setValue(true);
                        firstPriceListField.setValue(true);
                        scanInsNextField.setValue(true);
                        flatRateField.setValue(false);
                        scanNextField.setValue(true);
                        flatRateScanField.setValue(false);
                        break;
                    case "FREE":
                        index = _this.archediSubscriptions.findIndex(function (el) {
                            return el.key === "FREE";
                        });
                        structureField.setValue(false);
                        firstPriceListField.setValue(true);
                        scanInsNextField.setValue(false);
                        flatRateField.setValue(true);
                        scanNextField.setValue(true);
                        flatRateScanField.setValue(false);
                        break;
                    case "FREEBASIC":
                        index = _this.archediSubscriptions.findIndex(function (el) {
                            return el.key === "FREEBASIC";
                        });
                        structureField.setValue(false);
                        firstPriceListField.setValue(true);
                        scanInsNextField.setValue(true);
                        flatRateField.setValue(false);
                        scanNextField.setValue(true);
                        flatRateScanField.setValue(false);
                        break;
                }
                if (index > -1) {
                    feeField.setValue(_this.archediSubscriptions[index].fee);
                    availableSpaceField.setValue(_this.archediSubscriptions[index].availableSpace);
                    extraSpaceFeeField.setValue(_this.archediSubscriptions[index].extraSpaceFee);
                    structurePriceField.setValue(_this.archediSubscriptions[index].structurePrice);
                }
                // Backend requires a billing code whenever the matching flag is enabled.
                // Only one code is currently defined per list, so it is used directly
                // rather than filtered by plan (their "keys" arrays are inconsistently
                // maintained and do not cover every plan that sets these flags, e.g. FULLBASIC).
                var scanInsNextCode = _this.configService.availableInsCodesNext[0];
                var flatRateCode = _this.configService.availableInsCodesNextForfait[0];
                var scanNextCode = _this.configService.availableScanCodesNext[0];
                var flatRateScanCode = _this.configService.availableScanCodesNextForfait[0];
                _this.modelSC.subscription = _this.modelSC.subscription || {};
                _this.modelSC.subscription.scanInsNextCode =
                    scanInsNextField && scanInsNextField.value && scanInsNextCode
                        ? scanInsNextCode.value
                        : "INSSUCC001";
                _this.modelSC.subscription.flatRateCode =
                    flatRateField && flatRateField.value && flatRateCode
                        ? flatRateCode.value
                        : "FINSSUCC001";
                _this.modelSC.subscription.scanNextCode =
                    scanNextField && scanNextField.value && scanNextCode
                        ? scanNextCode.value
                        : "SCASUCC001";
                _this.modelSC.subscription.flatRateScanCode =
                    flatRateScanField && flatRateScanField.value && flatRateScanCode
                        ? flatRateScanCode.value
                        : "FSCASUCC001";
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        // Links between services ("Nota informativa", "Valutazione fornitori"): turning a
        // service on selects the ones it requires, which cannot be turned off while it is on
        (_a = this.subscriptions).push.apply(_a, tslib_1.__spread(bindServiceLinks(subscriptionControl, function (refusal) { return _this.onServiceLinkRefused(refusal); }, function (err) { return _this.helperService.handleError(err); })));
        if (suppliersControl) {
            suppliersControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(suppliersControl.value))
                .subscribe(function (res) {
                _this.suppliers = res;
                if (_this.suppliers) {
                    _this.hiddenCardInvitation = true;
                    _this.hiddenCardSuppliers = false;
                }
                else if (_this.nfcAccess) {
                    _this.hiddenCardInvitation = false;
                    _this.hiddenCardSuppliers = true;
                }
                if (!res) {
                    startDateSuppliersField.setValue(null);
                    endDateSuppliersField.setValue(null);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        if (startDateControl) {
            startDateControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(startDateControl.value))
                .subscribe(function (res) {
                if (!res) {
                    return;
                }
                if (res && startDateField.dirty) {
                    // Set endDate automatically to startDate + 1 year
                    var start = new Date(res);
                    var end = start;
                    end.setFullYear(start.getFullYear() + 1);
                    endDateField.setValue(end);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        if (startDateNfcAccessControl) {
            startDateNfcAccessControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(startDateNfcAccessControl.value))
                .subscribe(function (res) {
                if (!res) {
                    return;
                }
                if (res && startDateNfcAccessField.dirty) {
                    // Set endDate automatically to startDate + 1 year
                    var start = new Date(res);
                    var end = start;
                    end.setDate(start.getDate() - 1);
                    end.setFullYear(start.getFullYear() + 1);
                    endDateNfcAccessField.setValue(end);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        if (startDateSuppliersControl) {
            startDateSuppliersControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(startDateSuppliersControl.value))
                .subscribe(function (res) {
                if (!res) {
                    return;
                }
                if (res && startDateSuppliersField.dirty) {
                    // Set endDate automatically to startDate + 1 year
                    var start = new Date(res);
                    var end = start;
                    end.setDate(start.getDate() - 1);
                    end.setFullYear(end.getFullYear() + 1);
                    endDateSuppliersField.setValue(end);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        // if (privacyControl) {
        //   privacyControl.valueChanges
        //     .pipe(distinctUntilChanged(), startWith(privacyControl.value))
        //     .subscribe(
        //       (res) => {
        //         if (!res) {
        //           if (startDatePrivacyField !== null && endDatePrivacyField !== null) {
        //             startDatePrivacyField.setValue(null);
        //             endDatePrivacyField.setValue(null);
        //           }
        //         }
        //       },
        //       (err) => {
        //         this.helperService.handleError(err);
        //       }
        //     );
        // }
        // if (privacySagControl) {
        //   privacySagControl.valueChanges
        //     .pipe(distinctUntilChanged(), startWith(privacySagControl.value))
        //     .subscribe(
        //       (res) => {
        //         if (!res) {
        //           if (startDatePrivacyField !== null && endDatePrivacyField !== null) {
        //             startDatePrivacyField.setValue(null);
        //             endDatePrivacyField.setValue(null);
        //           }
        //         }
        //       },
        //       (err) => {
        //         this.helperService.handleError(err);
        //       }
        //     );
        // }
        if (startDatePrivacyControl) {
            startDatePrivacyControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(startDatePrivacyControl.value))
                .subscribe(function (res) {
                if (!res) {
                    return;
                }
                if (res && startDatePrivacyField !== null && startDatePrivacyField.dirty && endDatePrivacyField !== null) {
                    // Set endDate automatically to startDate + 1 year
                    var start = new Date(res);
                    var end = start;
                    end.setDate(start.getDate() - 1);
                    end.setFullYear(end.getFullYear() + 1);
                    endDatePrivacyField.setValue(end);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        if (nfcAccessControl) {
            nfcAccessControl.valueChanges
                .pipe(distinctUntilChanged(), startWith(nfcAccessControl.value))
                .subscribe(function (res) {
                _this.nfcAccess = res;
                if (_this.suppliers) {
                    _this.hiddenCardInvitation = true;
                    _this.hiddenCardSuppliers = false;
                }
                else if (_this.nfcAccess) {
                    _this.hiddenCardInvitation = false;
                    _this.hiddenCardSuppliers = true;
                }
                if (!res) {
                    var index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'checkbox'; });
                    if (index > -1) {
                        _this.suppliersDisplayedColumns.splice(index, 1);
                    }
                    index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'register'; });
                    if (index > -1) {
                        _this.suppliersDisplayedColumns.splice(index, 1);
                    }
                    index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'invitationDate'; });
                    if (index > -1) {
                        _this.suppliersDisplayedColumns.splice(index, 1);
                    }
                }
                else {
                    var index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'checkbox'; });
                    if (index === -1) {
                        _this.suppliersDisplayedColumns.splice(1, 0, 'checkbox');
                    }
                    index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'invitationDate'; });
                    if (index === -1) {
                        _this.suppliersDisplayedColumns.splice(10, 0, 'invitationDate');
                    }
                    index = _this.suppliersDisplayedColumns.findIndex(function (el) { return el === 'register'; });
                    if (index === -1) {
                        _this.suppliersDisplayedColumns.splice(11, 0, 'register');
                    }
                }
            }, function (err) {
                _this.helperService.handleError(err);
            });
        }
        // if (endDateControl) {
        //   endDateControl.valueChanges
        //     .pipe(distinctUntilChanged(), startWith(endDateControl.value))
        //     .subscribe(
        //       (res) => {
        //         if (!res) {
        //           return;
        //         }
        //         if (res && endDateField.dirty) {
        //           // Set startDate automatically to endDate - 1 year
        //           const end = new Date(res);
        //           const start = end;
        //           start.setFullYear(end.getFullYear() - 1);
        //           startDateField.setValue(start);
        //           if (startDateQrSafetyField) {
        //             const date = startDateField.value;
        //             startDateQrSafetyField.setValue(date);
        //           }
        //           if (endDateQrSafetyField) {
        //             const date = endDateField.value;
        //             endDateQrSafetyField.setValue(date);
        //           }
        //           if (startDateNfcAccessField) {
        //             const date = startDateField.value;
        //             startDateNfcAccessField.setValue(date);
        //           }
        //           if (endDateNfcAccessField) {
        //             const date = endDateField.value;
        //             endDateNfcAccessField.setValue(date);
        //           }
        //         }
        //       },
        //       (err) => {
        //         this.helperService.handleError(err);
        //       }
        //     );
        // }
    };
    /**
     * Handle form submit
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onFormSubmit = function (formNumber) {
        var _this = this;
        // Prevent invalid form submit
        if (formNumber === 1) {
            if (this.form.invalid) {
                // markAllAsTouched reaches the nested groups formly builds for dotted keys
                this.form.markAllAsTouched();
                console.warn("form", this.form);
                this.toastrService.error(this.translateService.instant("common.errors.form_is_invalid"), this.translateService.instant("common.words.error"));
                return;
            }
        }
        if (formNumber === 2) {
            if (this.form.invalid || this.formSC.invalid) {
                // markAllAsTouched reaches the nested groups formly builds for dotted keys
                this.form.markAllAsTouched();
                this.formSC.markAllAsTouched();
                console.warn(this.form);
                console.warn(this.formSC);
                this.toastrService.error(this.translateService.instant("common.errors.form_is_invalid"), this.translateService.instant("common.words.error"));
                return;
            }
        }
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("dialogs.confirm.title"),
                message: this.translateService.instant("companies.dialog.saveMessage"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            // Activate submit button loading status
            _this.formBtnOptions.active = true;
            // // Make a object copy
            // let model: any = {};
            // model = JSON.parse(JSON.stringify(companyModel));
            if (_this.model.pec === '') {
                delete _this.model.pec;
            }
            if (formNumber === 1) {
                // Remove subscription from model if company is not of "manager" type
                if (_this.model && _this.model.types && !_this.model.types.includes("manager")) {
                    if (_this.model.subscription) {
                        delete _this.model.subscription;
                    }
                }
            }
            if (formNumber === 2) {
                // Remove subscription from model if company is not of "manager" type
                if (_this.model && _this.model.types && !_this.model.types.includes("manager")) {
                    if (_this.model.subscription) {
                        delete _this.model.subscription;
                    }
                }
                else {
                    _this.model.subscription = _this.modelSC.subscription;
                    _this.nfcAccess = _this.model.subscription.nfcAccess;
                }
            }
            delete _this.model.requiredFields;
            _this.subscriptions.push(_this.companiesService
                .updateFields(_this.company.getId(), _this.model)
                .pipe(finalize(function () { return (_this.formBtnOptions.active = false); }))
                .subscribe(function (res) {
                if (res.status) {
                    _this.toastrService.success(res.message);
                    _this.enableEdit = false;
                    if (formNumber === 2) {
                        _this.setActiveServices(_this.model.subscription);
                    }
                }
            }, function (err) {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                // this.helperService.handleError(err);
                _this.helperService.handleFormlyError(_this.form, err);
            }));
        });
    };
    CompanyDetailComponent.prototype.removeIdsFromPrices = function (prices) {
        prices.forEach(function (price) {
            if (price && price._id) {
                delete price._id;
            }
        });
        this.company.set("subscription.prices", prices);
    };
    /**
     * Init subUsers data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initSubUsersDataTable = function () {
        var _this = this;
        if (!this.company || !this.company.getId()) {
            return;
        }
        if (!this.subUsersPaginator) {
            return;
        }
        if (!this.subUsersSort) {
            return;
        }
        // Load first set of data
        var start = this.subUsersPaginator.pageIndex * this.subUsersPaginator.pageSize;
        var length = this.subUsersPaginator.pageSize;
        this.subUsersDataSource.loadItems(this.company.getId(), start, length, this.subUsersSort.active, this.subUsersSort.direction);
        // Reset pagination after sorting
        this.subscriptions.push(this.subUsersSort.sortChange.subscribe(function () { return (_this.subUsersPaginator.pageIndex = 0); }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.subUsersSort.sortChange, this.subUsersPaginator.page)
            .pipe(tap(function () { return _this.loadSubUsersPage(); }))
            .subscribe());
        // Handle user's page change subUser
        // loading requested data
        this.subscriptions.push(this.subUsersPaginator.page
            .pipe(tap(function () { return _this.loadSubUsersPage(); }))
            .subscribe());
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadSubUsersPage = function () {
        var start = this.subUsersPaginator.pageIndex * this.subUsersPaginator.pageSize;
        var length = this.subUsersPaginator.pageSize;
        this.subUsersDataSource.loadItems(this.company.getId(), start, length, this.subUsersSort.active, this.subUsersSort.direction);
    };
    /**
     * Open subUser add dialog
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onAddSubUser = function () {
        var _this = this;
        if (!this.company || !this.company.getId()) {
            return;
        }
        // Open dialog
        var dialogRef = this.dialog.open(AddSubUserDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                companyId: this.company.getId(),
            },
        });
        // On dialog closing, check if something is changed
        // If so, let's reload table data
        this.subscriptions.push(dialogRef.afterClosed().subscribe(function (res) {
            if (typeof res !== "undefined" && res.createdSubUser) {
                _this.loadSubUsersPage();
                return;
            }
        }));
    };
    /**
     * Init subProperties data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initSubPropertiesDataTable = function () {
        var _this = this;
        if (!this.company || !this.company.getId()) {
            return;
        }
        if (!this.subPropertiesPaginator) {
            return;
        }
        if (!this.subPropertiesSort) {
            return;
        }
        // Load first set of data
        var start = this.subPropertiesPaginator.pageIndex *
            this.subPropertiesPaginator.pageSize;
        var length = this.subPropertiesPaginator.pageSize;
        this.subPropertiesDataSource.loadItems(this.company.getId(), start, length, this.subPropertiesSort.active, this.subPropertiesSort.direction);
        // Reset pagination after sorting
        this.subscriptions.push(this.subPropertiesSort.sortChange.subscribe(function () { return (_this.subPropertiesPaginator.pageIndex = 0); }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.subPropertiesSort.sortChange, this.subPropertiesPaginator.page)
            .pipe(tap(function () { return _this.loadSubPropertiesPage(); }))
            .subscribe());
        // Handle user's page change subProperty
        // loading requested data
        this.subscriptions.push(this.subPropertiesPaginator.page
            .pipe(tap(function () { return _this.loadSubPropertiesPage(); }))
            .subscribe());
        // Handle server-side filtering through general search
        this.subscriptions.push(fromEvent(this.subPropertiesInput.nativeElement, "keyup")
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged(), // Eliminate duplicate values
        tap(function () {
            _this.subPropertiesPaginator.pageIndex = 0; // Reset page
            _this.loadSubPropertiesPage();
        }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.adminFilter.valueChanges.pipe(startWith(null)),
            this.ownerFilter.valueChanges.pipe(startWith(null)),
            this.typeFilter.valueChanges.pipe(startWith(null)),
            this.nameFilter.valueChanges.pipe(startWith(null)),
            this.masterCodeFilter.valueChanges.pipe(startWith(null)),
            this.addressFilter.valueChanges.pipe(startWith(null)),
            this.addressCityFilter.valueChanges.pipe(startWith(null)),
            this.subscriptionEndDateFilter.valueChanges.pipe(startWith(null)),
            this.totalDocsInsertedFilter.valueChanges.pipe(startWith(null)),
            this.propertiesStatusFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
        )
            .subscribe(function (res) {
            // Handle admin filter
            if (res[0]) {
                // Since we cannot search in the same time for both admin and
                // owner, we must reset admin field. Maybe this should be handled in a better way
                _this.ownerFilter.setValue(undefined, { emitEvent: false });
                _this.adminIsLoading = true;
                _this.getAvailableAdminsAndOwners("admin", {
                    name: _this.adminFilter.value,
                });
                return;
            }
            // Handle owner filter
            if (res[1]) {
                // Since we cannot search in the same time for both admin and
                // owner, we must reset admin field. Maybe this should be handled in a better way
                _this.adminFilter.setValue(undefined, { emitEvent: false });
                _this.ownerIsLoading = true;
                _this.getAvailableAdminsAndOwners("owner", {
                    general: _this.ownerFilter.value,
                });
                return;
            }
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadSubPropertiesPage();
            }
        }));
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadSubPropertiesPage = function () {
        var start = this.subPropertiesPaginator.pageIndex *
            this.subPropertiesPaginator.pageSize;
        var length = this.subPropertiesPaginator.pageSize;
        // "MAPPATURA COMPLETA" is a value of the single STATO select but the
        // backend filters it via the dedicated search[mappingConfirmedOtherActivity]
        // parameter; ApiService drops undefined params so it is absent unless picked.
        var isCompleteMappingFilter = this.propertiesStatusFilter.value === "mappCompl";
        var search = {
            general: this.subPropertiesInput.nativeElement.value,
            admin: this.adminFilter.value ? this.adminFilter.value._id : undefined,
            owner: this.ownerFilter.value ? this.ownerFilter.value._id : undefined,
            type: this.typeFilter.value,
            name: this.nameFilter.value,
            masterCode: this.masterCodeFilter.value,
            address: this.addressFilter.value,
            addressCity: this.addressCityFilter.value,
            subscriptionEndDate: this.subscriptionEndDateFilter.value
                ? moment(this.subscriptionEndDateFilter.value).toISOString()
                : null,
            totalDocsInserted: this.totalDocsInsertedFilter.value,
            status: isCompleteMappingFilter
                ? undefined
                : this.propertiesStatusFilter.value,
            mappingConfirmedOtherActivity: isCompleteMappingFilter ? true : undefined,
        };
        this.subPropertiesDataSource.loadItems(this.company.getId(), start, length, this.subPropertiesSort.active, this.subPropertiesSort.direction, search, this.loggedUser._id);
    };
    /**
     * Subscribe to lists settings service to allow this list
     * to remember current filters once users navigate to another page.
     * In this way once user will come back to this page he will
     * have all previous settings (such as page number, page size, search, ...)
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.subscribeToSubPropsListsSettings = function (name) {
        var _this = this;
        this.listsService.enable();
        this.subscriptions.push(this.listsService.listsSettings$.subscribe(function (settings) {
            if (settings.length) {
                settings.forEach(function (setting) {
                    if (setting.name === name) {
                        if (setting.pageIndex) {
                            _this.subPropertiesPaginator.pageIndex = setting.pageIndex;
                        }
                        if (setting.pageSize) {
                            _this.subPropertiesPaginator.pageSize = setting.pageSize;
                        }
                        if (setting.search && setting.search.general) {
                            _this.subPropertiesInput.nativeElement.value =
                                setting.search.general;
                        }
                        if (setting.search && setting.search.admin) {
                            _this.adminFilter.setValue(setting.search.admin);
                        }
                        if (setting.search && setting.search.owner) {
                            _this.ownerFilter.setValue(setting.search.owner);
                        }
                        if (setting.search && setting.search.type) {
                            _this.typeFilter.setValue(setting.search.type);
                        }
                        if (setting.search && setting.search.name) {
                            _this.nameFilter.setValue(setting.search.name);
                        }
                        if (setting.search && setting.search.masterCode) {
                            _this.masterCodeFilter.setValue(setting.search.masterCode);
                        }
                        if (setting.search && setting.search.address) {
                            _this.addressFilter.setValue(setting.search.address);
                        }
                        if (setting.search && setting.search.addressCity) {
                            _this.addressCityFilter.setValue(setting.search.addressCity);
                        }
                        if (setting.search && setting.search.subscriptionEndDate) {
                            _this.subscriptionEndDateFilter.setValue(setting.search.subscriptionEndDate);
                        }
                        if (setting.search && setting.search.totalDocsInserted) {
                            _this.totalDocsInsertedFilter.setValue(setting.search.totalDocsInserted);
                        }
                        if (setting.search && setting.search.propertiesStatusFilter) {
                            _this.propertiesStatusFilter.setValue(setting.search.propertiesStatusFilter);
                        }
                    }
                });
            }
        }));
    };
    /**
     * Get the list of available admins and owners
     * used to allow filtering on owner and admin columns
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.getAvailableAdminsAndOwners = function (type, search) {
        var _this = this;
        this.subscriptions.push(this.companiesService
            .getList(0, 10, null, null, search)
            .pipe(finalize(function () {
            _this.adminIsLoading = false;
            _this.ownerIsLoading = false;
        }))
            .subscribe(function (res) {
            if (res.status) {
                if (type === "admin") {
                    _this.availableAdmins = res.data.items;
                }
                else if (type === "owner") {
                    _this.availableOwners = res.data.items;
                }
                else {
                    _this.availableAdmins = res.data.items;
                    _this.availableOwners = res.data.items;
                }
            }
        }));
    };
    /**
     * Handle click on a selected admin in the mat-autocomplete list and fires the loadPage()
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onAdminFilterSelected = function (event) {
        var value = event.source.value;
        this.adminFilter.setValue(value, { emitEvent: false });
        this.ownerFilter.setValue(undefined, { emitEvent: false });
        this.loadSubPropertiesPage();
    };
    /**
     * Handle click on a selected owner in the mat-autocomplete list and fires the loadSubPropertiesPage()
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onOwnerFilterSelected = function (event) {
        var value = event.source.value;
        this.adminFilter.setValue(undefined, { emitEvent: false });
        this.ownerFilter.setValue(value, { emitEvent: false });
        this.loadSubPropertiesPage();
    };
    /**
     * Used to handle the display of selected result
     *
     * @since 1.0.0
     *
     * @param arg Data to be displayed
     * @returns result string to display or undefined
     */
    CompanyDetailComponent.prototype.displayAdminOrOwnerFn = function (arg) {
        return arg ? "" + arg.name : undefined;
    };
    /**
     * Reset general search input and dispath a 'keyup' event
     * so that the table re-render his rows
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.resetSubPropertiesInput = function () {
        this.subPropertiesInput.nativeElement.value = "";
        var event = new KeyboardEvent("keyup");
        this.subPropertiesInput.nativeElement.dispatchEvent(event);
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetSubPropertiesFilters = function () {
        // Reset lists service data
        this.listsService.setListSetting("", 0, 10, {});
        // Reset tables' filters
        this.subPropertiesInput.nativeElement.value = "";
        this.adminFilter.setValue(null);
        this.ownerFilter.setValue(null);
        this.typeFilter.setValue(null);
        this.nameFilter.setValue(null);
        this.masterCodeFilter.setValue(null);
        this.addressFilter.setValue(null);
        this.addressCityFilter.setValue(null);
        this.subscriptionEndDateFilter.setValue(null);
        this.totalDocsInsertedFilter.setValue(null);
        this.propertiesStatusFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.subPropertiesSort.direction = "desc";
        this.subPropertiesSort.active = "subscriptionEndDate";
        this.subPropertiesSort._stateChanges.next();
        // Reset paginator
        this.subPropertiesPaginator.pageIndex = 0;
        this.subPropertiesPaginator.pageSize = 10;
        // Reload table's data
        this.loadSubPropertiesPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetSubPropertiesFilter = function (filter) {
        filter.reset();
        this.loadSubPropertiesPage();
    };
    /**
     * Remove current user from database
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onDelete = function () {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("companies.detail.deleteTab.confirmDeletion.title"),
                message: this.translateService.instant("companies.detail.deleteTab.confirmDeletion.message"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.subscriptions.push(_this.companiesService
                .delete(_this.company.getId())
                .pipe(finalize(function () { return (_this.deleteBtnOptions.active = false); }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to suspended list
                _this.router.navigate([
                    _this.lang,
                    "app",
                    "companies",
                    "suspended",
                ]);
                return;
            }, function (err) {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Remove current user from database
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onRemoveSubUser = function (element) {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("dialogs.confirm.title"),
                message: this.translateService.instant("dialogs.confirm.message"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.subscriptions.push(_this.companiesService
                .deleteSubUser(_this.company.getId(), element.userId)
                .pipe(finalize(function () { return (_this.deleteBtnOptions.active = false); }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                // Once the user is removed, reload data table
                _this.initSubUsersDataTable();
                return;
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Trigger the 'add phonebook' dialog
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onAddPhonebookContact = function () {
        var _this = this;
        if (!this.company || !this.company.getId()) {
            return;
        }
        // Open dialog
        var dialogRef = this.dialog.open(AddPhonebookContactDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                companyId: this.company.getId(),
            },
        });
        // On dialog closing, check if something is changed
        // If so, let's reload table data
        this.subscriptions.push(dialogRef.afterClosed().subscribe(function (res) {
            if (typeof res !== "undefined" && res.createdPhonebookContact) {
                _this.loadPhonebookPage();
                return;
            }
        }, function (err) {
            // this.helperService.handleError(err);
        }));
    };
    /**
     * Init suppliers data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initSuppliersDataTable = function () {
        var _this = this;
        // Reset pagination after sorting
        this.subscriptions.push(this.suppliersSort.sortChange.subscribe(function () { return _this.suppliersPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.suppliersSort.sortChange, this.suppliersPaginator.page)
            .pipe(tap(function () { return _this.loadSuppliersPage(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.companyNameFilter.valueChanges.pipe(startWith(null)),
            this.qualificationsFilter.valueChanges.pipe(startWith(null)),
            this.startCompanyDateFilter.valueChanges.pipe(startWith(null)),
            this.registrationFilter.valueChanges.pipe(startWith(null)),
            this.durcFilter.valueChanges.pipe(startWith(null)),
            this.creditLicenceFilter.valueChanges.pipe(startWith(null)),
            this.creditsFilter.valueChanges.pipe(startWith(null)),
            this.statusSuppliersFilter.valueChanges.pipe(startWith(null)),
            this.employeesFilter.valueChanges.pipe(startWith(null)),
            this.registerFilter.valueChanges.pipe(startWith(null)),
            this.invitationDateFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged())
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadSuppliersPage();
            }
        }));
        // Load first set of data
        this.loadSuppliersPage();
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadSuppliersPage = function () {
        var start = this.suppliersPaginator.pageIndex * this.suppliersPaginator.pageSize;
        var length = this.suppliersPaginator.pageSize;
        var search = {
            general: '',
            companyName: this.companyNameFilter.value,
            qualifications: this.qualificationsFilter.value,
            startCompanyDate: this.startCompanyDateFilter.value,
            registration: this.registrationFilter.value,
            durc: this.durcFilter.value,
            creditLicence: this.creditLicenceFilter.value,
            credits: this.creditsFilter.value,
            status: this.statusSuppliersFilter.value,
            employees: this.employeesFilter.value,
            register: this.registerFilter.value,
            invitationDate: this.invitationDateFilter.value,
            requestedCompany: this.company.get('_id'),
        };
        var master = true;
        this.suppliersDataSource.loadItems(start, length, this.suppliersSort.active, this.suppliersSort.direction, search, master);
    };
    /**
     * Export suppliers in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportSuppliers = function () {
        var _this = this;
        var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
        var fileName = this.translateService.instant('suppliers.list.exportFileName') + " " + date;
        this.subscriptions.push(this.suppliersService.getExportV2('listv3', this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFiltersSuppliers = function () {
        // Reset lists service data
        this.listsService.setListSetting('', 0, 10, {});
        // Reset tables' filters
        // this.input.nativeElement.value = '';
        this.companyNameFilter.setValue(null);
        this.qualificationsFilter.setValue(null);
        this.startCompanyDateFilter.setValue(null);
        this.registrationFilter.setValue(null);
        this.durcFilter.setValue(null);
        this.creditLicenceFilter.setValue(null);
        this.creditsFilter.setValue(null);
        this.statusSuppliersFilter.setValue(null);
        this.employeesFilter.setValue(null);
        this.registerFilter.setValue(null);
        this.invitationDateFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.suppliersSort.direction = 'desc';
        this.suppliersSort.active = 'companyName';
        this.suppliersSort._stateChanges.next();
        // Reset paginator
        this.suppliersPaginator.pageIndex = 0;
        this.suppliersPaginator.pageSize = 10;
        // Reload table's data
        this.loadSuppliersPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetSuppliersFilter = function (filter) {
        filter.reset();
        this.loadSuppliersPage();
    };
    /**
     * open pop up info of Qualifications
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onInfoPopUp = function () {
        var width = "600px";
        var title = this.translateService.instant('suppliers.fields.supplierDocuments.qualifications');
        var message = this.translateService.instant('suppliers.qualifications.qualificationA.description') + " <br><br>";
        message = message + " " + this.translateService.instant('suppliers.qualifications.qualificationB.description') + " <br><br>";
        message = message + " " + this.translateService.instant('suppliers.qualifications.qualificationC.description') + " <br><br>";
        message = message + " " + this.translateService.instant('suppliers.qualifications.qualificationD.description') + " <br><br>";
        message = message + " " + this.translateService.instant('suppliers.qualifications.qualificationE.description') + " <br><br>";
        message = message + " " + this.translateService.instant('suppliers.qualifications.qualificationF.description') + " <br><br>";
        message = message + " " + this.translateService.instant('suppliers.qualifications.qualificationG.description') + " <br><br>";
        if (title.trim() !== '') {
            this.dialog.open(InfoDialogComponent, {
                data: {
                    title: title,
                    message: message,
                },
                width: width,
                maxWidth: width,
            });
        }
    };
    /**
     * Handle delete supplier to Master list of supplier
     */
    CompanyDetailComponent.prototype.onDeleteAssociation = function (element) {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("suppliers.dialog.deleteAssociation.title"),
                message: this.translateService.instant("suppliers.dialog.deleteAssociation.message"),
                btnOkText: this.translateService.instant("common.cta.ok"),
                btnCancelText: this.translateService.instant("common.cta.close"),
            },
            width: "500px",
            disableClose: true,
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (res) {
                _this.suppliersService.deleteSupplierAssociation(_this.company.get('_id'), element._id).subscribe(function (res) {
                    if (res.status) {
                        _this.loadSuppliersPage();
                    }
                    else {
                        _this.helperService.handleError(res.message);
                    }
                }, function (err) {
                    _this.helperService.handleError(err);
                });
            }
        });
    };
    /**
     * Init subUsers data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initPhonebookDataTable = function () {
        var _this = this;
        if (!this.company || !this.company.getId()) {
            return;
        }
        if (!this.phonebookPaginator || !this.phonebookSort) {
            return;
        }
        // Reset pagination after sorting
        this.subscriptions.push(this.phonebookSort.sortChange.subscribe(function () { return (_this.phonebookPaginator.pageIndex = 0); }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.phonebookSort.sortChange, this.phonebookPaginator.page)
            .pipe(tap(function () { return _this.loadPhonebookPage(); }))
            .subscribe());
        // Handle user's page change subUser
        // loading requested data
        this.subscriptions.push(this.phonebookPaginator.page
            .pipe(tap(function () { return _this.loadPhonebookPage(); }))
            .subscribe());
        // Load first set of data
        this.loadPhonebookPage();
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadPhonebookPage = function () {
        var start = this.phonebookPaginator.pageIndex * this.phonebookPaginator.pageSize;
        var length = this.phonebookPaginator.pageSize;
        this.phonebookDataSource.loadItems(this.company.getId(), start, length, this.phonebookSort.active, this.phonebookSort.direction);
    };
    /**
     * Handle click on "Open" button near to each phonebook item
     * and open a view/edit dialog for clicked phonebook
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onOpenPhonebookContact = function (element) {
        var _this = this;
        if (!element) {
            return;
        }
        // Open dialog
        var dialogRef = this.dialog.open(EditPhonebookContactDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                phonebookContact: element,
                companyId: this.company.getId(),
            },
        });
        // On dialog closing, check if something is changed
        // If so, let's reload table data
        this.subscriptions.push(dialogRef.afterClosed().subscribe(function (res) {
            if (res) {
                _this.loadPhonebookPage();
                return;
            }
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Handle click on "Remove" button near to each phonebook item
     * and delete it
     */
    CompanyDetailComponent.prototype.onRemovePhonebookContact = function (element) {
        var _this = this;
        if (!element) {
            return;
        }
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("dialogs.confirm.title"),
                message: this.translateService.instant("dialogs.confirm.message"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.subscriptions.push(_this.companiesService
                .deletePhonebookContact(_this.company.getId(), element._id)
                .pipe(finalize(function () { return (_this.deleteBtnOptions.active = false); }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                // Once the user is removed, reload data table
                _this.initPhonebookDataTable();
                return;
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Handle new message dialog open
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onAddCommunication = function (element) {
        var _this = this;
        if (!element) {
            return;
        }
        // Open dialog
        var newMessageDialogRef = this.newMessageDialog.open(AddCommunicationDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                recipients: [
                    {
                        email: element.email,
                        name: element.firstName + " " + element.lastName,
                    },
                ],
                phonebook: this.company.get("phonebook"),
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    /**
     * Toggle activation in all list elements
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onToggleSelected = function (element) {
        if (!element.checked) {
            // Prevent invalid form submit
            if (this.form.invalid) {
                this.form.markAllAsTouched();
                this.toastrService.error(this.translateService.instant("common.errors.form_is_invalid"));
                element.source.toggle();
                return;
            }
        }
        this.enableEdit = element.checked;
    };
    /**
     * Subscribe to lists settings service to allow this list
     * to remember current filters once users navigate to another page.
     * In this way once user will come back to this page he will
     * have all previous settings (such as page number, page size, search, ...)
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.subscribeToListsSettings = function (name) {
        var _this = this;
        this.listsService.enable();
        this.subscriptions.push(this.listsService.listsSettings$.subscribe(function (settings) {
            if (settings.length) {
                settings.forEach(function (setting) {
                    if (setting.name === name) {
                        if (setting.pageIndex) {
                            _this.balancesPaginator.pageIndex = setting.pageIndex;
                        }
                        if (setting.pageSize) {
                            _this.balancesPaginator.pageSize = setting.pageSize;
                        }
                        if (setting.search && setting.search.general) {
                            _this.balancesInput.nativeElement.value = setting.search.general;
                        }
                        if (setting.search && setting.search.number) {
                            _this.numberFilter.setValue(setting.search.number);
                        }
                        if (setting.search && setting.search.property) {
                            _this.propertyFilter.setValue(setting.search.property);
                        }
                        if (setting.search && setting.search.status) {
                            _this.statusFilter.setValue(setting.search.status);
                        }
                        if (setting.search && setting.search.total) {
                            _this.totalFilter.setValue(setting.search.total);
                        }
                        if (setting.search && setting.search.discount) {
                            _this.discountFilter.setValue(setting.search.discount);
                        }
                        if (setting.search && setting.search.createdAt) {
                            _this.createdAtFilter.setValue(setting.search.createdAt);
                        }
                    }
                });
            }
        }));
    };
    /**
     * Init balances data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initBalancesDataTable = function () {
        var _this = this;
        // Load first set of data
        this.loadBalancesPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.balancesSort.sortChange.subscribe(function () { return (_this.balancesPaginator.pageIndex = 0); }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.balancesSort.sortChange, this.balancesPaginator.page)
            .pipe(tap(function () { return _this.loadBalancesPage(); }))
            .subscribe());
        // Handle server-side filtering through general search
        this.subscriptions.push(fromEvent(this.balancesInput.nativeElement, "keyup")
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged(), // Eliminate duplicate values
        tap(function () {
            _this.balancesPaginator.pageIndex = 0; // Reset page
            _this.loadBalancesPage();
        }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        combineLatest([
            this.numberFilter.valueChanges.pipe(startWith(null)),
            this.propertyFilter.valueChanges.pipe(startWith(null)),
            this.statusFilter.valueChanges.pipe(startWith(null)),
            this.totalFilter.valueChanges.pipe(startWith(null)),
            this.discountFilter.valueChanges.pipe(startWith(null)),
            this.createdAtFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
        )
            .subscribe(function (res) {
            if (res) {
                _this.propertyIsLoading = true;
                _this.getAvailableProperties({
                    general: _this.propertyFilter.value,
                });
            }
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadBalancesPage();
            }
        });
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadBalancesPage = function () {
        var start = this.balancesPaginator.pageIndex * this.balancesPaginator.pageSize;
        var length = this.balancesPaginator.pageSize;
        var search = {
            general: this.balancesInput.nativeElement.value,
            number: this.numberFilter.value,
            property: this.propertyFilter.value
                ? this.propertyFilter.value._id
                : undefined,
            status: this.statusFilter.value,
            total: this.totalFilter.value,
            discount: this.discountFilter.value,
            createdAt: this.createdAtFilter.value
                ? new Date(this.createdAtFilter.value).toISOString()
                : null,
        };
        this.balancesDataSource.loadCompanyItems(this.company.getId(), start, length, this.balancesSort.active, this.balancesSort.direction, search);
    };
    /**
     * Export properties in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExport = function (target, propertyId) {
        var _this = this;
        // With the switch live, the two Excels of a property come from the new archive (NEW-M7, FEX-05):
        // the legacy route reads the old archive, frozen at the switch
        var archiveV3Target = isArchiveV3Live() ? documentArchiveExportTargetOf(target) : null;
        if (archiveV3Target !== null) {
            this.exportFromArchiveV3(archiveV3Target, propertyId);
            return;
        }
        if (target && typeof propertyId !== "undefined") {
            // Handle export for missing/inserted documents
            var date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
            var fileName_1 = this.translateService.instant("properties.list." + target) + " " + date;
            this.subscriptions.push(this.propertiesService.getDocExport(target, propertyId).subscribe(function (res) {
                var fileBlob = res;
                var blob = new Blob([fileBlob], {
                    type: "application/vnd.ms-excel",
                });
                FileSaver.saveAs(blob, fileName_1 + ".xlsx");
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        }
    };
    /**
     * The «Esporta» entries of the menu of a property of the list as far as the switch decides them:
     * both, under the rules of the template, until the switch; once it is live, those of the «Esporta»
     * menu of the new dossier, for the plan of that property (@see legacyPageExportMenu). A property with
     * no plan counts as a paid one, as in the legacy; the template still hides both on `noSubscription`.
     *
     * @since 3.0.0
     *
     * @param element The property of the row; `null` for the rules of the role alone, as on a paid plan
     */
    CompanyDetailComponent.prototype.downloadMenuFor = function (element) {
        var role = this.currentRole && this.currentRole.role ? this.currentRole.role : null;
        return legacyPageExportMenu(isArchiveV3Live(), {
            roleType: role && typeof role.type === "string" ? role.type : "",
            permissions: this.loggedUserPermissions,
            subscriptionKey: element && typeof element.subscriptionKey === "string" ? element.subscriptionKey : "",
        });
    };
    /**
     * «Esporta inseriti» and «Esporta mancanti» of a property with the switch live: the Excel of the new
     * archive, as the «Esporta» menu of its dossier gives it. One download at a time. The plan of the row
     * is checked by the template; the role is checked again here, as the dossier does: a page left open
     * across a role change must not ask for an Excel the new role is not shown.
     *
     * @since 3.0.0
     *
     * @param target     Which Excel
     * @param propertyId The property of the row
     */
    CompanyDetailComponent.prototype.exportFromArchiveV3 = function (target, propertyId) {
        var _this = this;
        if (this.exportingTarget !== null || !propertyId || !this.downloadMenuFor(null)[target]) {
            return;
        }
        this.exportingTarget = target;
        if (this.exportSubscription) {
            this.exportSubscription.unsubscribe();
        }
        this.exportSubscription = this.documentArchiveExportDownloadService
            .download(target, propertyId)
            .pipe(finalize(function () {
            _this.exportingTarget = null;
        }))
            .subscribe();
    };
    /**
     * Export subProperties in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportSubProperties = function () {
        var _this = this;
        var date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
        var fileName = this.translateService.instant("properties.list.exportFileName") + " " + date;
        this.subscriptions.push(this.propertiesService.getExport(this.company.getId()).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Export balances in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportBalances = function () {
        var _this = this;
        var date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
        var fileName = this.translateService.instant("balances.list.exportFileName") + " " + date;
        this.subscriptions.push(this.balancesService.getExportCompany(this.company.getId()).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFilters = function () {
        // Reset lists service data
        this.listsService.setListSetting("balances", 0, 10, {});
        // Reset tables' filters
        this.balancesInput.nativeElement.value = "";
        this.numberFilter.setValue(null);
        this.propertyFilter.setValue(null);
        this.statusFilter.setValue(null);
        this.totalFilter.setValue(null);
        this.discountFilter.setValue(null);
        this.createdAtFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.balancesSort.direction = "desc";
        this.balancesSort.active = "createdAt";
        this.balancesSort._stateChanges.next();
        // Reset balancesPaginator
        this.balancesPaginator.pageIndex = 0;
        this.balancesPaginator.pageSize = 10;
        // Reload table's data
        this.loadBalancesPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFilter = function (filter) {
        filter.reset();
        this.loadBalancesPage();
    };
    /**
     * Reset general search input and dispath a 'keyup' event
     * so that the table re-render his rows
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.resetInput = function () {
        this.balancesInput.nativeElement.value = "";
        var event = new KeyboardEvent("keyup");
        this.balancesInput.nativeElement.dispatchEvent(event);
    };
    /**
     * Reactivate current user
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onReactivate = function () {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("companies.detail.deleteTab.confirmActivation.title"),
                message: this.translateService.instant("companies.detail.deleteTab.confirmActivation.message"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.subscriptions.push(_this.companiesService
                .activate(_this.company.getId())
                .pipe(finalize(function () { return (_this.reactivateBtnOptions.active = false); }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                _this.router.navigate([_this.lang, "app", "companies", "list"]);
                return;
            }, function (err) {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Used to handle the display of selected result
     *
     * @since 1.0.0
     *
     * @param arg Data to be displayed
     * @returns result string to display or undefined
     */
    CompanyDetailComponent.prototype.displayFn = function (arg) {
        return arg ? "" + arg.name : undefined;
    };
    /**
     * Handle click on a selected property in the mat-autocomplete list and fires the loadPage()
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPropertyFilterSelected = function (event) {
        var value = event.source.value;
        // this.propertyFilter.setValue(value, { emitEvent: false });
    };
    /**
     * Get the list of available admins and owners
     * used to allow filtering on owner and admin columns
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.getAvailableProperties = function (search) {
        var _this = this;
        this.subscriptions.push(this.companiesService
            .getSubPropertiesList(this.company.getId(), 0, null, null, null, search)
            .pipe(finalize(function () {
            _this.propertyIsLoading = false;
        }))
            .subscribe(function (res) {
            if (res.status) {
                if (res.data.items && Array.isArray(res.data.items)) {
                    _this.availableProperties = [];
                    res.data.items.forEach(function (item) {
                        item["_id"] = item.propertyId;
                        _this.availableProperties.push(item);
                    });
                }
                _this.availableProperties = res.data.items;
            }
        }));
    };
    /**
     * Init subUsers data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initAccessListDataTable = function () {
        var _this = this;
        // Load first set of data
        this.loadPageAccessList();
        // Reset pagination after sorting
        this.subscriptions.push(this.accessListSort.sortChange.subscribe(function () { return _this.accessListPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.accessListSort.sortChange, this.accessListPaginator.page)
            .pipe(tap(function () { return _this.loadPageAccessList(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.userFilter.valueChanges.pipe(startWith(null)),
            this.typeAccessFilter.valueChanges.pipe(startWith(null)),
            this.emailFilter.valueChanges.pipe(startWith(null)),
            this.companyFilter.valueChanges.pipe(startWith(null)),
            this.ipAddressFilter.valueChanges.pipe(startWith(null)),
            this.roleFilter.valueChanges.pipe(startWith(null)),
            this.createdAtAccessFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged())
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadPageAccessList();
            }
        }));
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadPageAccessList = function () {
        var start = this.accessListPaginator.pageIndex * this.accessListPaginator.pageSize;
        var length = this.accessListPaginator.pageSize;
        var search = {
            general: '',
            user: this.userFilter.value,
            type: this.typeAccessFilter.value,
            email: this.emailFilter.value,
            company: this.companyFilter.value,
            ipAddress: this.ipAddressFilter.value,
            role: this.roleFilter.value,
            createdAt: this.createdAtAccessFilter.value ? new Date(this.createdAtAccessFilter.value).toISOString() : undefined,
            requestedCompany: this.company.get('_id'),
        };
        this.accessListDataSource.loadItems(start, length, this.accessListSort.active, this.accessListSort.direction, search);
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFiltersAccessList = function () {
        // Reset lists service data
        this.listsService.setListSetting('', 0, 10, {});
        // Reset tables' filters
        // this.input.nativeElement.value = '';
        this.userFilter.setValue(null);
        this.typeAccessFilter.setValue(null);
        this.emailFilter.setValue(null);
        this.companyFilter.setValue(null);
        this.ipAddressFilter.setValue(null);
        this.roleFilter.setValue(null);
        this.createdAtAccessFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.accessListSort.direction = 'desc';
        this.accessListSort.active = 'createdAt';
        this.accessListSort._stateChanges.next();
        // Reset paginator
        this.accessListPaginator.pageIndex = 0;
        this.accessListPaginator.pageSize = 10;
        // Reload table's data
        this.loadPageAccessList();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFilterAccessList = function (filter) {
        filter.reset();
        this.loadPageAccessList();
    };
    /**
     * Export companies in a file with given format
     *
     * @since 1.0.0
     *
     * @param format The format. Can be 'excel' or 'csv'
     */
    CompanyDetailComponent.prototype.onExportAccessList = function () {
        var _this = this;
        var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
        var fileName = this.translateService.instant('accessLogs.list.exportFileName') + " " + date;
        this.subscriptions.push(this.accessLogsService.getExport(this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     *  Export contract in docx
     *
     * @since 1.0.0
    */
    CompanyDetailComponent.prototype.onGetContract = function () {
        var _this = this;
        this.detailCardIsLoading = true;
        PizZipUtils.getBinaryContent('https://api.archedi.it/uploads/CONTRATTO-UTILIZZO-PIATTAFORMA-ARCHEDI.docx', function (error, content) {
            try {
                if (error) {
                    throw error;
                }
                var zip = new PizZip(content);
                var doc = new Docxtemplater(zip, {
                    paragraphLoop: true,
                    linebreaks: true,
                });
                doc.setData({
                    company: _this.companyData.name,
                    legalAddress: _this.companyData.legalAddress.route + " " + _this.companyData.legalAddress.streetNumber + " - " + _this.companyData.legalAddress.postalCode + " " + _this.companyData.legalAddress.city + " " + _this.companyData.legalAddress.countryCodeTwo,
                    operativeAddres: _this.companyData.operativeAddress ? _this.companyData.operativeAddress.route + " " + _this.companyData.operativeAddress.streetNumber + " - " + _this.companyData.operativeAddress.postalCode + " " + _this.companyData.operativeAddress.city + " " + _this.companyData.operativeAddress.countryCodeTwo : _this.companyData.legalAddress.route + " " + _this.companyData.legalAddress.streetNumber + " - " + _this.companyData.legalAddress.postalCode + " " + _this.companyData.legalAddress.city + " " + _this.companyData.legalAddress.countryCodeTwo,
                    taxCode: _this.companyData.taxCode ? _this.companyData.taxCode : '……………………………',
                    vatNumber: _this.companyData.vatNumber ? _this.companyData.vatNumber : '……………………………',
                    date: moment(_this.disclaimerAt).isValid() ? moment(_this.disclaimerAt).format('DD-MM-YYYY hh:mm:ss') : '',
                    fullName: _this.disclaimerUser,
                });
                // render the document => replace all occurences
                doc.render();
                var out = doc.getZip().generate({
                    type: 'blob',
                    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                });
                // Output the document using Data-URI
                FileSaver.saveAs(out, 'CONTRATTO-UTILIZZO-PIATTAFORMA-ARCHEDI.docx');
                _this.detailCardIsLoading = false;
            }
            catch (error) {
                _this.detailCardIsLoading = false;
                _this.helperService.handleError(JSON.stringify(error));
            }
        });
    };
    /**
     * Init safetyVideoSuppliers data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initSafetyVideoSuppliersDataTable = function () {
        var _this = this;
        // Load first set of data
        this.loadSafetyVideoSuppliersPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.safetyVideoSuppliersSort.sortChange.subscribe(function () { return (_this.safetyVideoSuppliersPaginator.pageIndex = 0); }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.safetyVideoSuppliersSort.sortChange, this.safetyVideoSuppliersPaginator.page)
            .pipe(tap(function () { return _this.loadSafetyVideoSuppliersPage(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        combineLatest([
            this.safetyVideoSuppliersFirstNameFilter.valueChanges.pipe(startWith(null)),
            this.safetyVideoSuppliersLastNameFilter.valueChanges.pipe(startWith(null)),
            this.safetyVideoSuppliersCompanyNameFilter.valueChanges.pipe(startWith(null)),
            this.safetyVideoSuppliersCreatedAtFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
        )
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadSafetyVideoSuppliersPage();
            }
        });
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadSafetyVideoSuppliersPage = function () {
        var start = this.safetyVideoSuppliersPaginator.pageIndex * this.safetyVideoSuppliersPaginator.pageSize;
        var length = this.safetyVideoSuppliersPaginator.pageSize;
        var search = {
            firstName: this.safetyVideoSuppliersFirstNameFilter.value,
            lastName: this.safetyVideoSuppliersLastNameFilter.value,
            companyName: this.safetyVideoSuppliersCompanyNameFilter.value,
            createdAt: this.safetyVideoSuppliersCreatedAtFilter.value,
        };
        this.safetyVideoSuppliersDataSource.loadSuppliersVideoItems(this.company.get('_id'), start, length, this.safetyVideoSuppliersSort.active, this.safetyVideoSuppliersSort.direction, search);
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFiltersSafetyVideoSuppliers = function () {
        // Reset lists service data
        this.listsService.setListSetting("safetyVideoSuppliers", 0, 10, {});
        // Reset tables' filters
        // this.safetyVideoSuppliersInput.nativeElement.value = "";
        this.safetyVideoSuppliersFirstNameFilter.setValue(null);
        this.safetyVideoSuppliersLastNameFilter.setValue(null);
        this.safetyVideoSuppliersCompanyNameFilter.setValue(null);
        this.safetyVideoSuppliersCreatedAtFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.safetyVideoSuppliersSort.direction = "asc";
        this.safetyVideoSuppliersSort.active = "createdAt";
        this.safetyVideoSuppliersSort._stateChanges.next();
        // Reset safetyVideoSuppliersPaginator
        this.safetyVideoSuppliersPaginator.pageIndex = 0;
        this.safetyVideoSuppliersPaginator.pageSize = 10;
        // Reload table's data
        this.loadSafetyVideoSuppliersPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFilterSafetyVideoSuppliers = function (filter) {
        filter.reset();
        this.loadSafetyVideoSuppliersPage();
    };
    /**
     * Reset general search input and dispath a 'keyup' event
     * so that the table re-render his rows
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.resetInputSafetyVideoSuppliers = function () {
        this.safetyVideoSuppliersInput.nativeElement.value = "";
        var event = new KeyboardEvent("keyup");
        this.safetyVideoSuppliersInput.nativeElement.dispatchEvent(event);
    };
    /**
     * Handle click open QRcode safetyVideoSuppliers dialogbox
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onSafetyVideoSuppliersPrintQRcode = function () {
        this.dialog.open(PrintQrcodeDialogComponent, {
            data: {
                qrCode: this.safetyVideoSuppliersQrcodeData,
                name: this.companyData.name,
                city: this.companyData.legalAddress.city,
                address: this.companyData.legalAddress.route + " " + this.companyData.legalAddress.streetNumber
            },
            width: "700px",
        });
    };
    /**
     * Handle generate QRcode docx
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onSafetyVideoSuppliersGenerateQrCodeDoc = function () {
        var _this = this;
        var qrImage = document
            .getElementById("safetyVideoSuppliersQrcodeDataImage")
            .lastElementChild.lastElementChild.getAttribute("src");
        // Create document data
        var doc = new Document();
        var image = Media.addImage(doc, qrImage, 150, 150, {
            floating: {
                horizontalPosition: {
                    relative: HorizontalPositionRelativeFrom.PAGE,
                    align: HorizontalPositionAlign.CENTER,
                },
                verticalPosition: {
                    relative: VerticalPositionRelativeFrom.PARAGRAPH,
                    align: VerticalPositionAlign.CENTER,
                },
            },
        });
        doc.addSection({
            properties: {},
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_1,
                    spacing: { after: 400 },
                    children: [
                        new TextRun({
                            text: "AMMINISTRATORE " + this.company.get("name"),
                            bold: true,
                            allCaps: true,
                            color: "auto",
                            size: 24 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.TITLE,
                    children: [
                        new TextRun({
                            text: "ATTENZIONE",
                            font: "Calibri",
                            color: "FF0000",
                            bold: true,
                            allCaps: true,
                            size: 28 * 2,
                            underline: {
                                color: "FF0000",
                                type: UnderlineType.SINGLE,
                            },
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.TITLE,
                    spacing: { after: 500 },
                    children: [
                        new TextRun({
                            text: "AVVISO A TUTTI I FORNITORI",
                            font: "Calibri",
                            color: "FF0000",
                            bold: true,
                            allCaps: true,
                            size: 28 * 2,
                            underline: {
                                color: "FF0000",
                                type: UnderlineType.SINGLE,
                            },
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_1,
                    spacing: { after: 500 },
                    children: [
                        new TextRun({
                            text: "PRIMA DI ACCEDERE ALLE PARTI COMUNI DEL CONDOMINIO E' NECESSARIO VISUALIZZARE L'INFORMATIVA SULLA SICUREZZA E I DIVIETI",
                            font: "Calibri",
                            color: "FF0000",
                            bold: true,
                            allCaps: true,
                            size: 22 * 2,
                        }),
                    ],
                }),
                new Paragraph(image),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 2500 },
                    children: [
                        new TextRun({
                            text: "VIDEO INFORMATIVO AMMINISTRATORE " + this.company.get("name"),
                            font: "Times New Roman",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 12 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_2,
                    children: [
                        new TextRun({
                            text: this.company.get("legalAddress.route") + ", " + this.company.get("legalAddress.streetNumber"),
                            font: "Times New Roman",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 12 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 2000, after: 200 },
                    children: [
                        new TextRun({
                            text: "SI PREGA DI INQUADRARE IL QR-CODE DIRETTAMENTE CON LA FOTOCAMERA DEL VOSTRO DISPOSITIVO (SE ABILITATO) O ATTRAVERSO UN'APPLICAZIONE DEDICATA.",
                            font: "Calibri",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 14 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_3,
                    spacing: { after: 200 },
                    children: [
                        new TextRun({
                            text: "DOPO L'INQUADRATURA IL VIDEO SARA' DISPONIBILE PREVIA BREVE REGISTRAZIONE CON INSERIMENTO DI NOME, COGNOME, NOME AZIENDA.",
                            font: "Calibri",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 14 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_3,
                    children: [
                        new TextRun({
                            text: "BUONA VISIONE",
                            font: "Calibri",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 14 * 2,
                        }),
                    ],
                }),
            ],
        });
        // Used to export the file into a .docx file
        Packer.toBlob(doc).then(function (blob) {
            FileSaver.saveAs(blob, "Cartello-Video-Sicurezza-Fornitori_" + _this.company.get("name") + ".docx");
        });
    };
    /**
     * Handle Qrcode send by email
     */
    CompanyDetailComponent.prototype.onSafetyVideoSuppliersSendQrcodeByEmail = function () {
        var _this = this;
        var qrImage = document
            .getElementById("safetyVideoSuppliersQrcodeDataImage")
            .lastElementChild.lastElementChild.getAttribute("src");
        // Open dialog
        var newMessageDialogRef = this.newMessageDialog.open(AddCommunicationDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                qrImage: qrImage,
                phonebook: this.company.get("phonebook"),
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    /**
     * Export safetyVideoSuppliers in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportSafetyVideoSuppliers = function () {
        var _this = this;
        var date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
        var fileName = this.translateService.instant("companies.detail.exportSafetyVideoSuppliersFile") + " " + date;
        this.subscriptions.push(this.videoViewService.getSuppliersVideoExport(this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Init safetyVideoResidents data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initSafetyVideoResidentsDataTable = function () {
        var _this = this;
        // Load first set of data
        this.loadSafetyVideoResidentsPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.safetyVideoResidentsSort.sortChange.subscribe(function () { return (_this.safetyVideoResidentsPaginator.pageIndex = 0); }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.safetyVideoResidentsSort.sortChange, this.safetyVideoResidentsPaginator.page)
            .pipe(tap(function () { return _this.loadSafetyVideoResidentsPage(); }))
            .subscribe());
        // // Handle server-side filtering through general search
        // this.subscriptions.push(
        //   fromEvent(this.safetyVideoResidentsInput.nativeElement, "keyup")
        //     .pipe(
        //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        //       distinctUntilChanged(), // Eliminate duplicate values
        //       tap(() => {
        //         this.safetyVideoResidentsPaginator.pageIndex = 0; // Reset page
        //         this.loadSafetyVideoResidentsPage();
        //       })
        //     )
        //     .subscribe()
        // );
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        combineLatest([
            this.safetyVideoResidentsFirstNameFilter.valueChanges.pipe(startWith(null)),
            this.safetyVideoResidentsLastNameFilter.valueChanges.pipe(startWith(null)),
            this.safetyVideoResidentsCompanyNameFilter.valueChanges.pipe(startWith(null)),
            this.safetyVideoResidentsCreatedAtFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
        )
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadSafetyVideoResidentsPage();
            }
        });
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadSafetyVideoResidentsPage = function () {
        var start = this.safetyVideoResidentsPaginator.pageIndex * this.safetyVideoResidentsPaginator.pageSize;
        var length = this.safetyVideoResidentsPaginator.pageSize;
        var search = {
            firstName: this.safetyVideoResidentsFirstNameFilter.value,
            lastName: this.safetyVideoResidentsLastNameFilter.value,
            companyName: this.safetyVideoResidentsCompanyNameFilter.value,
            createdAt: this.safetyVideoResidentsCreatedAtFilter.value,
        };
        this.safetyVideoResidentsDataSource.loadResidentsVideoItems(this.company.get('_id'), start, length, this.safetyVideoResidentsSort.active, this.safetyVideoResidentsSort.direction, search);
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFiltersSafetyVideoResidents = function () {
        // Reset lists service data
        this.listsService.setListSetting("safetyVideoResidents", 0, 10, {});
        // Reset tables' filters
        // this.safetyVideoResidentsInput.nativeElement.value = "";
        this.safetyVideoResidentsFirstNameFilter.setValue(null);
        this.safetyVideoResidentsLastNameFilter.setValue(null);
        this.safetyVideoResidentsCompanyNameFilter.setValue(null);
        this.safetyVideoResidentsCreatedAtFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.safetyVideoResidentsSort.direction = "asc";
        this.safetyVideoResidentsSort.active = "createdAt";
        this.safetyVideoResidentsSort._stateChanges.next();
        // Reset safetyVideoResidentsPaginator
        this.safetyVideoResidentsPaginator.pageIndex = 0;
        this.safetyVideoResidentsPaginator.pageSize = 10;
        // Reload table's data
        this.loadSafetyVideoResidentsPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFilterSafetyVideoResidents = function (filter) {
        filter.reset();
        this.loadSafetyVideoResidentsPage();
    };
    /**
     * Reset general search input and dispath a 'keyup' event
     * so that the table re-render his rows
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.resetInputSafetyVideoResidents = function () {
        this.safetyVideoResidentsInput.nativeElement.value = "";
        var event = new KeyboardEvent("keyup");
        this.safetyVideoResidentsInput.nativeElement.dispatchEvent(event);
    };
    /**
     * Handle click open QRcode safetyVideoResidents dialogbox
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onSafetyVideoResidentsPrintQRcode = function () {
        this.dialog.open(PrintQrcodeDialogComponent, {
            data: {
                qrCode: this.safetyVideoResidentsQrcodeData,
                name: this.companyData.name,
                city: this.companyData.legalAddress.city,
                address: this.companyData.legalAddress.route + " " + this.companyData.legalAddress.streetNumber
            },
            width: "700px",
        });
    };
    /**
     * Handle generate QRcode docx
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onSafetyVideoResidentsGenerateQrCodeDoc = function () {
        var _this = this;
        var qrImage = document
            .getElementById("safetyVideoResidentsQrcodeDataImage")
            .lastElementChild.lastElementChild.getAttribute("src");
        // Create document data
        var doc = new Document();
        var image = Media.addImage(doc, qrImage, 150, 150, {
            floating: {
                horizontalPosition: {
                    relative: HorizontalPositionRelativeFrom.PAGE,
                    align: HorizontalPositionAlign.CENTER,
                },
                verticalPosition: {
                    relative: VerticalPositionRelativeFrom.PARAGRAPH,
                    align: VerticalPositionAlign.CENTER,
                },
            },
        });
        doc.addSection({
            properties: {},
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_1,
                    spacing: { after: 400 },
                    children: [
                        new TextRun({
                            text: "AMMINISTRATORE " + this.company.get("name"),
                            bold: true,
                            allCaps: true,
                            color: "auto",
                            size: 24 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.TITLE,
                    children: [
                        new TextRun({
                            text: "ATTENZIONE",
                            font: "Calibri",
                            color: "FF0000",
                            bold: true,
                            allCaps: true,
                            size: 28 * 2,
                            underline: {
                                color: "FF0000",
                                type: UnderlineType.SINGLE,
                            },
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.TITLE,
                    spacing: { after: 500 },
                    children: [
                        new TextRun({
                            text: "AVVISO A TUTTI I CONDOMINI",
                            font: "Calibri",
                            color: "FF0000",
                            bold: true,
                            allCaps: true,
                            size: 28 * 2,
                            underline: {
                                color: "FF0000",
                                type: UnderlineType.SINGLE,
                            },
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_1,
                    spacing: { after: 500 },
                    children: [
                        new TextRun({
                            text: "PRIMA DI ACCEDERE ALLE PARTI COMUNI DEL CONDOMINIO E' NECESSARIO VISUALIZZARE L'INFORMATIVA SULLA SICUREZZA E I DIVIETI",
                            font: "Calibri",
                            color: "FF0000",
                            bold: true,
                            allCaps: true,
                            size: 22 * 2,
                        }),
                    ],
                }),
                new Paragraph(image),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 2500 },
                    children: [
                        new TextRun({
                            text: "VIDEO INFORMATIVO AMMINISTRATORE " + this.company.get("name"),
                            font: "Times New Roman",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 12 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_2,
                    children: [
                        new TextRun({
                            text: this.company.get("legalAddress.route") + ", " + this.company.get("legalAddress.streetNumber"),
                            font: "Times New Roman",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 12 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 2000, after: 200 },
                    children: [
                        new TextRun({
                            text: "SI PREGA DI INQUADRARE IL QR-CODE DIRETTAMENTE CON LA FOTOCAMERA DEL VOSTRO DISPOSITIVO (SE ABILITATO) O ATTRAVERSO UN'APPLICAZIONE DEDICATA.",
                            font: "Calibri",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 14 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_3,
                    spacing: { after: 200 },
                    children: [
                        new TextRun({
                            text: "DOPO L'INQUADRATURA IL VIDEO SARA' DISPONIBILE PREVIA BREVE REGISTRAZIONE CON INSERIMENTO DI NOME, COGNOME, NOME AZIENDA.",
                            font: "Calibri",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 14 * 2,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    heading: HeadingLevel.HEADING_3,
                    children: [
                        new TextRun({
                            text: "BUONA VISIONE",
                            font: "Calibri",
                            color: "auto",
                            bold: true,
                            allCaps: true,
                            size: 14 * 2,
                        }),
                    ],
                }),
            ],
        });
        // Used to export the file into a .docx file
        Packer.toBlob(doc).then(function (blob) {
            FileSaver.saveAs(blob, "Cartello-Video-Sicurezza-Fornitori_" + _this.company.get("name") + ".docx");
        });
    };
    /**
     * Handle Qrcode send by email
     */
    CompanyDetailComponent.prototype.onSafetyVideoResidentsSendQrcodeByEmail = function () {
        var _this = this;
        var qrImage = document
            .getElementById("safetyVideoResidentsQrcodeDataImage")
            .lastElementChild.lastElementChild.getAttribute("src");
        // Open dialog
        var newMessageDialogRef = this.newMessageDialog.open(AddCommunicationDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                qrImage: qrImage,
                phonebook: this.company.get("phonebook"),
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    /**
     * Export safetyVideoResidents in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportSafetyVideoResidents = function () {
        var _this = this;
        var date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
        var fileName = this.translateService.instant("companies.detail.exportSafetyVideoResidentsFile") + " " + date;
        this.subscriptions.push(this.videoViewService.getResidentsVideoExport(this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    // ### START - Study archive ###
    /**
     * Init genericDocs data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initGenericDocsDataTable = function () {
        var _this = this;
        // Load first set of data
        this.genericDocsLoadPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.genericDocsSort.sortChange.subscribe(function () { return _this.genericDocsPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.genericDocsSort.sortChange, this.genericDocsPaginator.page)
            .pipe(tap(function () { return _this.genericDocsLoadPage(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.genericDocsNameFilter.valueChanges.pipe(startWith(null)),
            this.genericDocsDescriptionFilter.valueChanges.pipe(startWith(null)),
            this.genericDocsCreatedAtFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged())
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.genericDocsLoadPage();
            }
        }));
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.genericDocsLoadPage = function () {
        var start = this.genericDocsPaginator.pageIndex * this.genericDocsPaginator.pageSize;
        var length = this.genericDocsPaginator.pageSize;
        var search = {
            name: this.genericDocsNameFilter.value,
            description: this.genericDocsDescriptionFilter.value,
            createdAt: this.genericDocsCreatedAtFilter.value,
        };
        this.genericDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.genericDocsSort.active, this.genericDocsSort.direction, search);
    };
    /**
     * Export generic docs in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onGenericDocsExport = function () {
        var _this = this;
        var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
        var fileName = this.translateService.instant('companies.detail.exportGenericDocsFile') + " " + date;
        this.subscriptions.push(this.genericDocsService.getExport(this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onGenericDocsResetFilters = function () {
        // Reset lists service data
        this.listsService.setListSetting('genericDocs', 0, 10, {});
        // Reset tables' filters
        // this.input.nativeElement.value = '';
        this.genericDocsNameFilter.setValue(null);
        this.genericDocsDescriptionFilter.setValue(null);
        this.genericDocsCreatedAtFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.genericDocsSort.direction = "desc";
        this.genericDocsSort.active = "createdAt";
        this.genericDocsSort._stateChanges.next();
        // Reset paginator
        this.genericDocsPaginator.pageIndex = 0;
        this.genericDocsPaginator.pageSize = 10;
        // Reload table's data
        this.genericDocsLoadPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onGenericDocsResetFilter = function (filter) {
        filter.reset();
        this.genericDocsLoadPage();
    };
    /**
     * Reset general search input and dispath a 'keyup' event
     * so that the table re-render his rows
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.genericDocsResetInput = function () {
        // this.genericDocsInput.nativeElement.value = '';
        var event = new KeyboardEvent('keyup');
        // this.genericDocsInput.nativeElement.dispatchEvent(event);
    };
    /**
     * Handler Add new doc
     */
    CompanyDetailComponent.prototype.onGenericDocsAddDoc = function () {
        var _this = this;
        var dialogRef = this.dialog.open(GenericDocsAddDocDialogComponent, {
            data: {
                companyId: this.company.get("_id")
            },
            width: "700px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            else {
                _this.genericDocsLoadPage();
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Handler Edit doc
     */
    CompanyDetailComponent.prototype.onGenericDocsEdit = function (data) {
        var _this = this;
        var dialogRef = this.dialog.open(GenericDocsEditDocDialogComponent, {
            data: data,
            width: "700px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            else {
                _this.genericDocsLoadPage();
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Handler Delete doc
     */
    CompanyDetailComponent.prototype.onGenericDocsDelete = function (docId) {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant('genericDocs.deleteDoc.title'),
                message: this.translateService.instant('genericDocs.deleteDoc.message'),
                btnOkText: this.translateService.instant('common.cta.confirm'),
                btnCancelText: this.translateService.instant('common.cta.close'),
            },
            width: '500px',
            disableClose: true
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res2) {
            if (res2) {
                _this.genericDocsService.delete(_this.company.get("_id"), docId)
                    .subscribe(function (res) {
                    if (res.status) {
                        _this.genericDocsLoadPage();
                    }
                }, function (err) {
                    _this.helperService.handleError(err);
                });
            }
        });
    };
    /**
     * Handler View standard QRcode doc
     */
    CompanyDetailComponent.prototype.onGenericDocsViewQrcode = function (data) {
        this.dialog.open(GenericDocsQrCodeDialogComponent, {
            data: {
                data: data,
                print: false,
            },
            width: "450px",
        });
    };
    /**
     * Handler Print standard QRcode doc
     */
    CompanyDetailComponent.prototype.onGenericDocsPrintQrcode = function (data) {
        this.dialog.open(GenericDocsQrCodeDialogComponent, {
            data: {
                data: data,
                print: true,
            },
            width: "450px",
        });
    };
    /**
     * Handler download files
     */
    CompanyDetailComponent.prototype.onGenericDocsDownload = function (data) {
        this.dialog.open(DocumentDownloadDialogComponent, {
            data: {
                documents: data.files,
            },
            width: "600px",
        });
    };
    /**
     * Update docs list to send by email
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onGenericDocSelected = function (doc, checked) {
        if (doc && checked) {
            this.selectedGenericDocs.push(doc);
        }
        else if (doc && !checked) {
            var index = this.selectedGenericDocs.indexOf(doc._id);
            this.selectedGenericDocs.splice(index, 1);
        }
    };
    /**
     * Handel send docs by email
     */
    CompanyDetailComponent.prototype.onGenericDocsSendByEmail = function () {
        var _this = this;
        var body = "<ul>";
        this.selectedGenericDocs.forEach(function (doc) {
            if (!doc.files || !doc.files[0]) {
                // This would never happen
                _this.toastrService.error("common.errors.unknown");
                return;
            }
            var name = doc.files[0].originalName;
            var path = doc.files[0].path.replace(/src[\\\/]assets/g, 'public');
            var url = "";
            if (path.substring(0, 22) === "https://app.archedi.it") {
                // url = path;
                var docFilename = path.substr(path.lastIndexOf("/"));
                url = "https://api.archedi.it/uploads/genericdocs/companies/" + _this.company.get("_id") + "/" + docFilename;
            }
            else {
                url = _this.configService.apiHost + "/" + path;
            }
            body = body.concat("<li>" + name + ": <a href=\"" + url + "\">" + url + "</a></li><br>");
        });
        body = body.concat("</ul>");
        // Open dialog
        var newMessageDialogRef = this.newMessageDialog.open(AddCommunicationDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                body: body,
                phonebook: this.company.get("phonebook"),
                sendDocs: true,
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    // ### END - Study archive ###
    // ### START - Safety Fire ###
    /**
     * Init fireSafetyDocs data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initFireSafetyDocsDataTable = function () {
        var _this = this;
        // Load first set of data
        this.fireSafetyDocsLoadPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.fireSafetyDocsSort.sortChange.subscribe(function () { return _this.fireSafetyDocsPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.fireSafetyDocsSort.sortChange, this.fireSafetyDocsPaginator.page)
            .pipe(tap(function () { return _this.fireSafetyDocsLoadPage(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.fireSafetyDocsNameFilter.valueChanges.pipe(startWith(null)),
            this.fireSafetyDocsDescriptionFilter.valueChanges.pipe(startWith(null)),
            this.fireSafetyDocsCreatedAtFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged())
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.fireSafetyDocsLoadPage();
            }
        }));
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.fireSafetyDocsLoadPage = function () {
        var start = this.fireSafetyDocsPaginator.pageIndex * this.fireSafetyDocsPaginator.pageSize;
        var length = this.fireSafetyDocsPaginator.pageSize;
        var search = {
            name: this.fireSafetyDocsNameFilter.value,
            description: this.fireSafetyDocsDescriptionFilter.value,
            createdAt: this.fireSafetyDocsCreatedAtFilter.value,
        };
        this.fireSafetyDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.fireSafetyDocsSort.active, this.fireSafetyDocsSort.direction, search);
    };
    /**
     * Export generic docs in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsExport = function () {
        var _this = this;
        var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
        var fileName = this.translateService.instant('companies.detail.exportFireSafetyDocsFile') + " " + date;
        this.subscriptions.push(this.fireSafetyDocsService.getExport(this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsResetFilters = function () {
        // Reset lists service data
        this.listsService.setListSetting('fireSafetyDocs', 0, 10, {});
        // Reset tables' filters
        // this.input.nativeElement.value = '';
        this.fireSafetyDocsNameFilter.setValue(null);
        this.fireSafetyDocsDescriptionFilter.setValue(null);
        this.fireSafetyDocsCreatedAtFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.fireSafetyDocsSort.direction = "desc";
        this.fireSafetyDocsSort.active = "createdAt";
        this.fireSafetyDocsSort._stateChanges.next();
        // Reset paginator
        this.fireSafetyDocsPaginator.pageIndex = 0;
        this.fireSafetyDocsPaginator.pageSize = 10;
        // Reload table's data
        this.fireSafetyDocsLoadPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsResetFilter = function (filter) {
        filter.reset();
        this.fireSafetyDocsLoadPage();
    };
    /**
     * Reset general search input and dispath a 'keyup' event
     * so that the table re-render his rows
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.fireSafetyDocsResetInput = function () {
        // this.fireSafetyDocsInput.nativeElement.value = '';
        var event = new KeyboardEvent('keyup');
        // this.fireSafetyDocsInput.nativeElement.dispatchEvent(event);
    };
    /**
     * Handler Add new doc
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsAddDoc = function () {
        var _this = this;
        var dialogRef = this.dialog.open(FireSafetyDocsAddDocDialogComponent, {
            data: {
                companyId: this.company.get("_id")
            },
            width: "700px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            else {
                _this.fireSafetyDocsLoadPage();
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Handler Edit doc
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsEdit = function (data) {
        var _this = this;
        var dialogRef = this.dialog.open(FireSafetyDocsEditDocDialogComponent, {
            data: data,
            width: "700px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            else {
                _this.fireSafetyDocsLoadPage();
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Handler Delete doc
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsDelete = function (docId) {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant('companies.detail.fireSafetyDocsCard.dialog.deleteDoc.title'),
                message: this.translateService.instant('companies.detail.fireSafetyDocsCard.dialog.deleteDoc.message'),
                btnOkText: this.translateService.instant('common.cta.confirm'),
                btnCancelText: this.translateService.instant('common.cta.close'),
            },
            width: '500px',
            disableClose: true
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res2) {
            if (res2) {
                _this.fireSafetyDocsService.delete(_this.company.get("_id"), docId)
                    .subscribe(function (res) {
                    if (res.status) {
                        _this.fireSafetyDocsLoadPage();
                    }
                }, function (err) {
                    _this.helperService.handleError(err);
                });
            }
        });
    };
    /**
     * Handler View standard QRcode doc
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsViewQrcode = function (data) {
        this.dialog.open(FireSafetyDocsQrCodeDialogComponent, {
            data: {
                data: data,
                print: false,
            },
            width: "450px",
        });
    };
    /**
     * Handler Print standard QRcode doc
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsPrintQrcode = function (data) {
        this.dialog.open(FireSafetyDocsQrCodeDialogComponent, {
            data: {
                data: data,
                print: true,
            },
            width: "450px",
        });
    };
    /**
     * Handler download files
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsDownload = function (data) {
        this.dialog.open(DocumentDownloadDialogComponent, {
            data: {
                documents: data.files,
            },
            width: "600px",
        });
    };
    /**
     * Update docs list to send by email
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onFireSafetyDocSelected = function (doc, checked) {
        if (doc && checked) {
            this.selectedFireSafetyDocs.push(doc);
        }
        else if (doc && !checked) {
            var index = this.selectedFireSafetyDocs.indexOf(doc._id);
            this.selectedFireSafetyDocs.splice(index, 1);
        }
    };
    CompanyDetailComponent.prototype.onFireSafetyDefaultDocSendByEmail = function (element) {
        var _this = this;
        var insertedDocs = [];
        var selectedDocs = [];
        selectedDocs.push(element._id);
        var data = {
            document: element.files,
        };
        insertedDocs.push({
            docId: element._id,
            title: element.name,
            data: data,
        });
        var newMessageDialogRef = this.newMessageDialog.open(SendEmailDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                phonebook: this.company.get("phonebook")
                    ? this.company.get("phonebook")
                    : [],
                insertedDocs: insertedDocs,
                selectedDocs: selectedDocs,
                companyData: this.loggedUserMaster ? this.companyData : "",
                propertyName: "",
                propertyId: "",
                sentFromSupplierPage: false,
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    /**
     * Handel send docs by email
     */
    CompanyDetailComponent.prototype.onFireSafetyDocsSendByEmail = function () {
        // let body = `<ul>`;
        // this.selectedFireSafetyDocs.forEach((doc: any) => {
        //   if (!doc.files || !doc.files[0]) {
        //     // This would never happen
        //     this.toastrService.error("common.errors.unknown");
        //     return;
        //   }
        //   const name = doc.files[0].originalName;
        //   const path = doc.files[0].path.replace(/src[\\\/]assets/g, 'public');
        //   let url = "";
        //   if (path.substring(0, 22) === "https://app.archedi.it") {
        //     // url = path;
        //     const docFilename = path.substr(path.lastIndexOf("/"));
        //     url = `https://api.archedi.it/uploads/firesafetydocs/companies/${this.company.get("_id")}/${docFilename}`;
        //   } else {
        //     url = `${this.configService.apiHost}/${path}`;
        //   }
        //   body = body.concat(
        //     `<li>${name}: <a href="${url}">${url}</a></li><br>`
        //   );
        // });
        // body = body.concat(`</ul>`);
        var _this = this;
        // Open dialog
        // const newMessageDialogRef = this.newMessageDialog.open(
        //   AddCommunicationDialogComponent,
        //   {
        //     minWidth: "60%",
        //     maxHeight: "98vh",
        //     data: {
        //       body,
        //       phonebook: this.company.get("phonebook"),
        //       sendDocs: true,
        //     },
        //     disableClose: true,
        //   }
        // );
        var insertedDocs = [];
        var selectedDocs = [];
        this.selectedFireSafetyDocs.forEach(function (doc) {
            selectedDocs.push(doc._id);
            var data = {
                document: doc.files,
            };
            insertedDocs.push({
                docId: doc._id,
                title: doc.name,
                data: data,
            });
        });
        var newMessageDialogRef = this.newMessageDialog.open(SendEmailDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                phonebook: this.company.get("phonebook")
                    ? this.company.get("phonebook")
                    : [],
                insertedDocs: insertedDocs,
                selectedDocs: selectedDocs,
                companyData: this.loggedUserMaster ? this.companyData : "",
                propertyName: "",
                propertyId: "",
                sentFromSupplierPage: false,
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    // ### END - Fire Safety ###
    // ### START - Privacy ###
    /**
     * Init privacyDocs data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initPrivacyDocsDataTable = function () {
        var _this = this;
        if (this.hiddenCardPrivacy) {
            // Privacy / Privacy SAG / Nota informativa not active for this company: skip the API call entirely
            return;
        }
        // Load first set of data
        this.privacyDocsLoadPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.privacyDocsSort.sortChange.subscribe(function () { return _this.privacyDocsPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.privacyDocsSort.sortChange, this.privacyDocsPaginator.page)
            .pipe(tap(function () { return _this.privacyDocsLoadPage(); }))
            .subscribe());
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.privacyDocsLoadPage = function () {
        var start = this.privacyDocsPaginator.pageIndex * this.privacyDocsPaginator.pageSize;
        var length = this.privacyDocsPaginator.pageSize;
        // Companies with "privacySag" see the property and supplier privacy docs; the back-end only
        // supports filtering by a single type value, so both are requested together and
        // filtered/paginated client-side (@see PrivacyDocsDataSource). Without "privacySag":
        // "suppliers" shows the supplier doc, the "Nota informativa" shows the condo-owner
        // access key (registered by the back-end on the first generation of the Nota).
        var search;
        var allowedTypes;
        if (this.privacySagActive) {
            allowedTypes = [PROPERTY_PRIVACY_TEMPLATE, SUPPLIER_PRIVACY_TEMPLATE];
        }
        else {
            var types = [];
            if (this.informativeNoteActive) {
                types.push(PROPERTY_PRIVACY_TEMPLATE);
            }
            if (this.suppliers) {
                types.push(SUPPLIER_PRIVACY_TEMPLATE);
            }
            if (types.length > 1) {
                allowedTypes = types;
            }
            else if (types.length === 1) {
                search = { type: types[0] };
            }
        }
        this.privacyDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.privacyDocsSort.active, this.privacyDocsSort.direction, search, allowedTypes);
    };
    /**
     * Init the "Nota informativa" docs data table (condo-owner privacy notice only)
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.initInformativeNoteDocsDataTable = function () {
        var _this = this;
        if (!this.informativeNoteActive || !this.informativeNoteDocsSort || !this.informativeNoteDocsPaginator) {
            // "Nota informativa" not active (or its card is not rendered): skip the API call entirely
            return;
        }
        // Load first set of data
        this.informativeNoteDocsLoadPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.informativeNoteDocsSort.sortChange.subscribe(function () { return _this.informativeNoteDocsPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.informativeNoteDocsSort.sortChange, this.informativeNoteDocsPaginator.page)
            .pipe(tap(function () { return _this.informativeNoteDocsLoadPage(); }))
            .subscribe());
    };
    /**
     * Load requested "Nota informativa" docs data page
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.informativeNoteDocsLoadPage = function () {
        var start = this.informativeNoteDocsPaginator.pageIndex * this.informativeNoteDocsPaginator.pageSize;
        var length = this.informativeNoteDocsPaginator.pageSize;
        this.informativeNoteDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.informativeNoteDocsSort.active, this.informativeNoteDocsSort.direction, { type: PROPERTY_PRIVACY_TEMPLATE });
    };
    /**
     * Check that a privacy doc link is a safe, absolute http(s) URL
     * before using it to navigate or render it inside the UI
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.isValidPrivacyDocLink = function (link) {
        if (!link) {
            return false;
        }
        try {
            var url = new URL(link);
            return url.protocol === 'http:' || url.protocol === 'https:';
        }
        catch (err) {
            return false;
        }
    };
    /**
     * Build the public, unauthenticated URL for this privacy doc's general
     * access page (@see PrivacyDocsPublicAccessComponent): the visitor lands
     * there, enters a property's tax code, and downloads the document once
     * it's confirmed to belong to this company.
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.buildPrivacyDocPublicLink = function (docType) {
        console.log("docType", docType);
        var companyId = this.company.get('_id');
        var segmentByType = {
            "informativa_privacy_immobile": 'condo-owner',
            "informativa_privacy_fornitore": 'supplier',
        };
        var segment = segmentByType[docType];
        if (!companyId || !segment) {
            return null;
        }
        return window.location.origin + "/privacy-docs/" + companyId + "/" + segment;
    };
    /**
     * Handle "Accedi" action: open the privacy doc's public access page in a new tab
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyDocsAccess = function (element) {
        var link = this.buildPrivacyDocPublicLink(element && element.type);
        if (!link || !this.isValidPrivacyDocLink(link)) {
            this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
            return;
        }
        window.open(link, '_blank', 'noopener,noreferrer');
    };
    /**
     * Handle "Visualizza QR-code" action: open a dialog showing the QR-code
     * that encodes the privacy doc's public access page link
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyDocsViewQrcode = function (element) {
        var link = this.buildPrivacyDocPublicLink(element && element.type);
        if (!link || !this.isValidPrivacyDocLink(link)) {
            this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
            return;
        }
        this.dialog.open(PrivacyDocsQrCodeDialogComponent, {
            minWidth: '40%',
            data: {
                data: tslib_1.__assign({}, element, { link: link }),
            },
        });
    };
    /**
     * Handle "Visualizza link" action: open a dialog showing the privacy doc's
     * public access page link
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyDocsViewLink = function (element) {
        var link = this.buildPrivacyDocPublicLink(element && element.type);
        if (!link || !this.isValidPrivacyDocLink(link)) {
            this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
            return;
        }
        this.dialog.open(PrivacyDocsLinkDialogComponent, {
            minWidth: '40%',
            data: {
                data: tslib_1.__assign({}, element, { link: link }),
            },
        });
    };
    /**
     * Handle "Genera documenti" action: let the user pick which properties
     * of this company to generate the privacy documents for, then trigger
     * the generation on the back-end and download the resulting file
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyDocsGenerate = function () {
        var _this = this;
        if (!this.privacySagActive && !this.suppliers) {
            return;
        }
        if (!this.availableProperties || !this.availableProperties.length) {
            this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.noProperties'));
            return;
        }
        var dialogRef = this.dialog.open(GeneratePrivacyDocsDialogComponent, {
            data: {
                modalTitle: this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.title'),
                properties: this.availableProperties,
            },
            width: '800px',
            disableClose: true
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res2) {
            if (!res2 || !res2.propertyIds || !res2.propertyIds.length) {
                return;
            }
            // Companies with only "privacy" + "suppliers" (not "privacySag") can only generate the supplier privacy doc
            var templateType = _this.privacySagActive ? undefined : SUPPLIER_PRIVACY_TEMPLATE;
            _this.privacyDocsService.generateDocuments(_this.company.get('_id'), res2.propertyIds, templateType)
                .subscribe(function (fileBlob) {
                var extension = PRIVACY_DOC_MIME_EXTENSIONS[fileBlob.type] || DEFAULT_PRIVACY_DOC_EXTENSION;
                _this.toastrService.success(_this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.success'));
                if (extension === 'zip') {
                    return;
                }
                var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
                var fileName = _this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.exportFileName') + "-" + date + "." + extension;
                FileSaver.saveAs(fileBlob, fileName);
            }, function (err) {
                _this.helperService.handleError(err);
            });
        });
    };
    /**
     * Reload the tables listing the condo-owner privacy access key: the back-end
     * registers it on the first "Nota informativa" generation
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.refreshPrivacyAccessKeyTables = function () {
        if (!this.hiddenCardPrivacy && this.privacyDocsPaginator && this.privacyDocsSort) {
            this.privacyDocsLoadPage();
        }
        if (this.informativeNoteActive && this.informativeNoteDocsPaginator && this.informativeNoteDocsSort) {
            this.informativeNoteDocsLoadPage();
        }
    };
    /**
     * Handle "Genera Nota informativa" action: download the generated PDF
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.onInformativeNoteDownload = function () {
        var _this = this;
        if (!this.informativeNoteActive || this.informativeNoteDocIsLoading) {
            return;
        }
        this.informativeNoteDocIsLoading = true;
        this.subscriptions.push(this.informativeNoteService.generateDocumentFile(this.company.get('_id'))
            .pipe(finalize(function () { return _this.informativeNoteDocIsLoading = false; }))
            .subscribe(function (res) {
            FileSaver.saveAs(res.blob, res.fileName);
            _this.refreshPrivacyAccessKeyTables();
        }, function (err) { return _this.helperService.handleError(err); }));
    };
    /**
     * Handle "Invia Nota informativa" action: generate the document, upload it and
     * open the email dialog with the document already attached
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.onInformativeNoteSendByEmail = function () {
        var _this = this;
        if (!this.informativeNoteActive || this.informativeNoteDocIsLoading) {
            return;
        }
        this.informativeNoteDocIsLoading = true;
        this.subscriptions.push(this.informativeNoteService.generateDocumentFile(this.company.get('_id'))
            .pipe(tap(function () { return _this.refreshPrivacyAccessKeyTables(); }), concatMap(function (res) { return _this.informativeNoteService.uploadForEmail(res.blob, res.fileName); }), finalize(function () { return _this.informativeNoteDocIsLoading = false; }))
            .subscribe(function (files) {
            var dialogRef = _this.newMessageDialog.open(SendEmailDialogComponent, {
                minWidth: "60%",
                maxHeight: "98vh",
                data: {
                    phonebook: _this.company.get("phonebook") ? _this.company.get("phonebook") : [],
                    insertedDocs: [],
                    selectedDocs: [],
                    attachments: files,
                    hideInsertedDocs: true,
                    companyData: _this.loggedUserMaster ? _this.companyData : "",
                    propertyName: "",
                    propertyId: "",
                    sentFromSupplierPage: false,
                },
                disableClose: true,
            });
            dialogRef.beforeClosed()
                .pipe(take(1))
                .subscribe(function (res) {
                if (res) {
                    _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
                }
            });
        }, function (err) { return _this.helperService.handleError(err); }));
    };
    /**
     * Handle "Invia PEC" action: when plain Privacy is active (not Privacy SAG),
     * first check the percentage of suppliers that already have their privacy
     * doc generated and warn the user if it's below 80%; in any case, ask for
     * confirmation before triggering the mass sending of the privacy documents
     * access PEC email to every supplier of this company
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyDocsSendPec = function () {
        var _this = this;
        if (this.privacySagActive) {
            this.openPrivacyDocsSendPecConfirmDialog();
            return;
        }
        this.privacyDocsSendPecDisabled = true;
        this.subscriptions.push(this.privacyDocsService.getPercentage(this.company.get('_id'), SUPPLIER_PRIVACY_TEMPLATE)
            .pipe(finalize(function () { return _this.privacyDocsSendPecDisabled = false; }))
            .subscribe(function (res) {
            var percentage = res && res.data ? res.data.percentage : null;
            _this.openPrivacyDocsSendPecConfirmDialog(percentage);
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Open the "Invia PEC" confirmation dialog and, once confirmed, trigger
     * the mass sending of the privacy documents access PEC email to every
     * supplier of this company
     *
     * @since 1.0.0
     *
     * @param [percentage] Percentage of suppliers with their privacy doc
     *                      already generated; when below 80 a stronger
     *                      warning message is shown to the user
     */
    CompanyDetailComponent.prototype.openPrivacyDocsSendPecConfirmDialog = function (percentage) {
        var _this = this;
        if (percentage === void 0) { percentage = null; }
        var lowPercentage = typeof percentage === 'number' && percentage < 80;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant('dialogs.confirm.title'),
                message: lowPercentage
                    ? this.translateService.instant('companies.detail.privacyDocsCard.sendPecLowPercentageConfirm', { percentage: percentage })
                    : this.translateService.instant('companies.detail.privacyDocsCard.sendPecConfirm'),
                btnOkText: this.translateService.instant('common.cta.confirm'),
                btnCancelText: this.translateService.instant('common.cta.undo'),
            },
            width: '500px',
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.privacyDocsSendPecDisabled = true;
            _this.subscriptions.push(_this.suppliersService.sendPrivacyEmailAll(_this.company.get('_id'))
                .pipe(finalize(function () { return _this.privacyDocsSendPecDisabled = false; }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        });
    };
    // ### END - Privacy ###
    // ### START - Privacy Studio Docs ###
    /**
     * Handle "Scarica template" action: open a dialog listing the
     * company-level ("studio") privacy document templates available for
     * download, pre-filled with this company's own registry data
     * (Privacy SAG subscription required, gated in the template via
     * privacySagActive)
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onOpenStudioPrivacyTemplatesDialog = function () {
        if (!this.privacySagActive) {
            return;
        }
        this.dialog.open(StudioPrivacyTemplatesDialogComponent, {
            data: {
                companyId: this.company.get("_id"),
            },
            width: "650px",
            disableClose: true,
        });
    };
    /**
     * Init privacyStudioDocs data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initPrivacyStudioDocsDataTable = function () {
        var _this = this;
        if (this.hiddenCardPrivacy) {
            // Privacy / Privacy SAG not active for this company: skip the API call entirely
            return;
        }
        // Load first set of data
        this.privacyStudioDocsLoadPage();
        // Reset pagination after sorting
        this.subscriptions.push(this.privacyStudioDocsSort.sortChange.subscribe(function () { return _this.privacyStudioDocsPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.privacyStudioDocsSort.sortChange, this.privacyStudioDocsPaginator.page)
            .pipe(tap(function () { return _this.privacyStudioDocsLoadPage(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.privacyStudioDocsTypeFilter.valueChanges.pipe(startWith(null)),
            this.privacyStudioDocsDescriptionFilter.valueChanges.pipe(startWith(null)),
            this.privacyStudioDocsCreatedAtFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged())
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.privacyStudioDocsLoadPage();
            }
        }));
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.privacyStudioDocsLoadPage = function () {
        var start = this.privacyStudioDocsPaginator.pageIndex * this.privacyStudioDocsPaginator.pageSize;
        var length = this.privacyStudioDocsPaginator.pageSize;
        var search = {
            type: this.privacyStudioDocsTypeFilter.value,
            description: this.privacyStudioDocsDescriptionFilter.value,
            createdAt: this.privacyStudioDocsCreatedAtFilter.value,
        };
        this.privacyStudioDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.privacyStudioDocsSort.active, this.privacyStudioDocsSort.direction, search);
    };
    /**
     * Export privacy studio docs in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsExport = function () {
        var _this = this;
        var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
        var fileName = this.translateService.instant('companies.detail.exportPrivacyStudioDocsFile') + " " + date;
        this.subscriptions.push(this.privacyStudioDocsService.getExport(this.company.get('_id')).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsResetFilters = function () {
        // Reset lists service data
        this.listsService.setListSetting('privacyStudioDocs', 0, 10, {});
        // Reset tables' filters
        this.privacyStudioDocsTypeFilter.setValue(null);
        this.privacyStudioDocsDescriptionFilter.setValue(null);
        this.privacyStudioDocsCreatedAtFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.privacyStudioDocsSort.direction = "desc";
        this.privacyStudioDocsSort.active = "createdAt";
        this.privacyStudioDocsSort._stateChanges.next();
        // Reset paginator
        this.privacyStudioDocsPaginator.pageIndex = 0;
        this.privacyStudioDocsPaginator.pageSize = 10;
        // Reload table's data
        this.privacyStudioDocsLoadPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsResetFilter = function (filter) {
        filter.reset();
        this.privacyStudioDocsLoadPage();
    };
    /**
     * Handler Add new doc
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsAddDoc = function () {
        var _this = this;
        var dialogRef = this.dialog.open(PrivacyStudioDocsAddDocDialogComponent, {
            data: {
                companyId: this.company.get("_id")
            },
            width: "700px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            else {
                _this.privacyStudioDocsLoadPage();
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Handler Edit doc
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsEdit = function (data) {
        var _this = this;
        var dialogRef = this.dialog.open(PrivacyStudioDocsEditDocDialogComponent, {
            data: data,
            width: "700px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            else {
                _this.privacyStudioDocsLoadPage();
            }
        }, function (err) {
            _this.helperService.handleError(err);
        });
    };
    /**
     * Handler Delete doc
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsDelete = function (docId) {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant('companies.detail.privacyDocsCard.studioSection.dialog.deleteDoc.title'),
                message: this.translateService.instant('companies.detail.privacyDocsCard.studioSection.dialog.deleteDoc.message'),
                btnOkText: this.translateService.instant('common.cta.confirm'),
                btnCancelText: this.translateService.instant('common.cta.close'),
            },
            width: '500px',
            disableClose: true
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res2) {
            if (res2) {
                _this.privacyStudioDocsService.delete(_this.company.get("_id"), docId)
                    .subscribe(function (res) {
                    if (res.status) {
                        _this.privacyStudioDocsLoadPage();
                    }
                }, function (err) {
                    _this.helperService.handleError(err);
                });
            }
        });
    };
    /**
     * Handler View QRcode doc
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsViewQrcode = function (data) {
        this.dialog.open(PrivacyStudioDocsQrCodeDialogComponent, {
            data: {
                data: data,
                print: false,
            },
            width: "450px",
        });
    };
    /**
     * Handler download files
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsDownload = function (data) {
        this.dialog.open(DocumentDownloadDialogComponent, {
            data: {
                documents: data.files,
            },
            width: "600px",
        });
    };
    /**
     * Update docs list to send by email
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocSelected = function (doc, checked) {
        if (doc && checked) {
            this.selectedPrivacyStudioDocs.push(doc);
        }
        else if (doc && !checked) {
            var index = this.selectedPrivacyStudioDocs.indexOf(doc._id);
            this.selectedPrivacyStudioDocs.splice(index, 1);
        }
    };
    /**
     * Handle send selected docs by email
     */
    CompanyDetailComponent.prototype.onPrivacyStudioDocsSendByEmail = function () {
        var _this = this;
        if (!this.selectedPrivacyStudioDocs.length) {
            this.toastrService.error(this.translateService.instant('common.errors.chooseElementFirst'));
            return;
        }
        var insertedDocs = [];
        var selectedDocs = [];
        this.selectedPrivacyStudioDocs.forEach(function (doc) {
            selectedDocs.push(doc._id);
            var data = {
                document: doc.files,
            };
            insertedDocs.push({
                docId: doc._id,
                title: _this.translateService.instant("companies.fields.privacyStudioDocs.types." + doc.type),
                data: data,
            });
        });
        var newMessageDialogRef = this.newMessageDialog.open(SendEmailDialogComponent, {
            minWidth: "60%",
            maxHeight: "98vh",
            data: {
                phonebook: this.company.get("phonebook")
                    ? this.company.get("phonebook")
                    : [],
                insertedDocs: insertedDocs,
                selectedDocs: selectedDocs,
                companyData: this.loggedUserMaster ? this.companyData : "",
                propertyName: "",
                propertyId: "",
                sentFromSupplierPage: false,
            },
            disableClose: true,
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        newMessageDialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.toastrService.success(_this.translateService.instant("communications.add.sendedSuccessfully"));
        });
    };
    // ### END - Privacy Studio Docs ###
    /**
     * open pop up Video
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onVideoPopUp = function (videoType) {
        var title = '';
        var videoName = '';
        switch (videoType) {
            case 'SafetyVideoSuppliers':
                title = this.translateService.instant("companies.detail.safetyVideoSuppliersCard.title");
                videoName = 'Archedi-SD3';
                break;
            case 'SafetyVideoResidents':
                title = this.translateService.instant("companies.detail.safetyVideoResidentsCard.title");
                videoName = 'Archedi-Resident';
                break;
        }
        var width = "800px";
        this.dialog.open(VideoDialogComponent, {
            data: {
                title: title,
                videoName: videoName
            },
            width: width,
            maxWidth: width,
        });
    };
    /**
     * Reload historical notes
     */
    CompanyDetailComponent.prototype.OnPlatformNoteDeactivate = function (event) {
        if (event) {
            this.historicalNotes.loadPage();
        }
    };
    /**
     * Open modal of property notes
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onOpenNotes = function (propertyId) {
        this.dialog.open(ViewPlatformNotesDialogComponent, {
            data: {
                propertyId: propertyId
            },
            width: '90%',
        });
    };
    /**
     * Open modal of company balances notes
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onViewBalancesNotes = function () {
        this.dialog.open(ViewPlatformNotesDialogComponent, {
            data: {
                companyIdBalances: this.companyId
            },
            width: '90%',
        });
    };
    /**
     * Handle list of properties subscription to renew
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onSubsSelected = function (element, checked) {
        if (element && checked) {
            this.selectedSuppliers.push({
                id: element._id,
                name: element.companyName,
                email: element.pec,
                value: true,
            });
        }
        else if (element && !checked) {
            var index = this.selectedSuppliers.findIndex(function (el) { return el.id.toString() === element._id.toString(); });
            if (index > -1) {
                this.selectedSuppliers.splice(index, 1);
            }
        }
    };
    /**
     * Handle invite to suppliers
     */
    CompanyDetailComponent.prototype.onInviteSuppliers = function () {
        var _this = this;
        if (!this.selectedSuppliers.length) {
            this.toastrService.error(this.translateService.instant('common.errors.chooseElementFirst'));
            return;
        }
        var sendData = {
            suppliers: this.selectedSuppliers,
            companyId: this.companyId,
            companyName: this.companyData.name,
        };
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("companies.detail.inviteSuppliersAdmin.title"),
                message: this.translateService.instant("companies.detail.inviteSuppliersAdmin.description"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.subscriptions.push(_this.suppliersService.inviteSuppliers(sendData)
                .subscribe(function (res) {
                if (res.status) {
                    _this.selectedSuppliers = [];
                    _this.toastrService.success(res.message);
                    _this.loadSuppliersPage();
                }
                else {
                    _this.helperService.handleError(res.message);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Enable/Disable send supplier email
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onToggleSendEmail = function (action) {
        var _this = this;
        var data = {
            disableSendEmailNfcAccess: false
        };
        var message = this.translateService.instant("companies.detail.enableSendEmail");
        if (action === 'disable') {
            data = {
                disableSendEmailNfcAccess: true
            };
            message = this.translateService.instant("companies.detail.disableSendEmail");
        }
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant('dialogs.confirm.title'),
                message: message,
                btnOkText: this.translateService.instant('common.cta.confirm'),
                btnCancelText: this.translateService.instant('common.cta.undo'),
            },
            width: '500px',
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.detailCardIsLoading = true;
            _this.subscriptions.push(_this.companiesService.updateFields(_this.company.getId(), data)
                .pipe(finalize(function () { return _this.detailCardIsLoading = false; }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                _this.disableSendEmail = res2.data.disableSendEmailNfcAccess;
                return;
            }, function (err) {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Toggle visibility of the masterCode (condominium code) column
     * for master/collaborator users, persisting the choice on the company
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.onToggleShowMasterCode = function () {
        var _this = this;
        var data = { showMasterCode: !this.showMasterCode };
        var message = this.translateService.instant(this.showMasterCode ? 'companies.detail.hideMasterCode' : 'companies.detail.showMasterCode');
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant('dialogs.confirm.title'),
                message: message,
                btnOkText: this.translateService.instant('common.cta.confirm'),
                btnCancelText: this.translateService.instant('common.cta.undo'),
            },
            width: '500px',
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef.beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.detailCardIsLoading = true;
            _this.subscriptions.push(_this.companiesService.updateFields(_this.company.getId(), data)
                .pipe(finalize(function () { return _this.detailCardIsLoading = false; }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                _this.showMasterCode = res2.data.showMasterCode;
                var masterCodeIndex = _this.subPropertiesDisplayedColumns.findIndex(function (el) { return el === 'masterCode'; });
                if (_this.showMasterCode && masterCodeIndex === -1) {
                    var nameIndex = _this.subPropertiesDisplayedColumns.findIndex(function (el) { return el === 'name'; });
                    _this.subPropertiesDisplayedColumns.splice(nameIndex + 1, 0, 'masterCode');
                }
                else if (!_this.showMasterCode && masterCodeIndex > -1) {
                    _this.subPropertiesDisplayedColumns.splice(masterCodeIndex, 1);
                }
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        });
    };
    /**
     * Show the company's internal ID in an info dialog (Admin/Operatore only,
     * gated in the template via loggedUserAdminOrOperator)
     *
     * @since 3.0.0
     */
    CompanyDetailComponent.prototype.onShowCompanyId = function () {
        this.dialog.open(InfoDialogComponent, {
            data: {
                title: this.translateService.instant('companies.detail.companyIdDialogTitle'),
                message: this.company.getId(),
            },
            width: '400px',
            maxWidth: '400px',
        });
    };
    /**
     * Init invitation data table
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.initInvitationDataTable = function () {
        var _this = this;
        // Reset pagination after sorting
        this.subscriptions.push(this.invitationSort.sortChange.subscribe(function () { return _this.invitationPaginator.pageIndex = 0; }));
        // Once user change sorting OR change page
        // let's reload the page data
        this.subscriptions.push(merge(this.invitationSort.sortChange, this.invitationPaginator.page)
            .pipe(tap(function () { return _this.loadInvitationPage(); }))
            .subscribe());
        // Handle server-side filtering through column-specific search
        // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
        this.subscriptions.push(combineLatest([
            this.invitationSupplierFilter.valueChanges.pipe(startWith(null)),
            this.invitationEmailFilter.valueChanges.pipe(startWith(null)),
            this.invitationPecFilter.valueChanges.pipe(startWith(null)),
            this.invitationVatNumberFilter.valueChanges.pipe(startWith(null)),
            this.invitationRegisterFilter.valueChanges.pipe(startWith(null)),
        ])
            .pipe(debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged())
            .subscribe(function (res) {
            // Load page only if at least one value is not null
            if (!res.every(function (value) { return value === null; })) {
                _this.loadInvitationPage();
            }
        }));
        // Load first set of data
        this.loadInvitationPage();
    };
    /**
     * Load requested data page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.loadInvitationPage = function () {
        var start = this.invitationPaginator.pageIndex * this.invitationPaginator.pageSize;
        var length = this.invitationPaginator.pageSize;
        var search = {
            general: '',
            companyName: this.invitationSupplierFilter.value,
            pec: this.invitationPecFilter.value,
            email: this.invitationEmailFilter.value,
            vatNumber: this.invitationVatNumberFilter.value,
            register: this.invitationRegisterFilter.value,
        };
        var master = true;
        this.invitationDataSource.loadCompanyItems(this.companyId, start, length, this.invitationSort.active, this.invitationSort.direction, search);
    };
    /**
     * Export suppliers in a file with given format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportInvitation = function () {
        var _this = this;
        var date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
        var fileName = this.translateService.instant('suppliersInvitation.exportFileName') + " " + date;
        this.subscriptions.push(this.suppliersService.getCompanyInvitationExport(this.companyId).subscribe(function (res) {
            var fileBlob = res;
            var blob = new Blob([fileBlob], {
                type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, fileName + ".xlsx");
        }, function (err) {
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Reset table's filters
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetFiltersInvitaion = function () {
        // Reset lists service data
        this.listsService.setListSetting('', 0, 10, {});
        // Reset tables' filters
        // this.input.nativeElement.value = '';
        this.invitationSupplierFilter.setValue(null);
        this.invitationEmailFilter.setValue(null);
        this.invitationPecFilter.setValue(null);
        this.invitationVatNumberFilter.setValue(null);
        this.invitationRegisterFilter.setValue(null);
        // Reset tables' sorting versus and column
        this.invitationSort.direction = 'desc';
        this.invitationSort.active = 'companyName';
        this.invitationSort._stateChanges.next();
        // Reset paginator
        this.invitationPaginator.pageIndex = 0;
        this.invitationPaginator.pageSize = 10;
        // Reload table's data
        this.loadInvitationPage();
    };
    /**
     * Reset given filter value and reload the page
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onResetInvitaionFilter = function (filter) {
        filter.reset();
        this.loadInvitationPage();
    };
    /**
     * open pop up info of Registration
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onInfoRegistrationPopUp = function () {
        var width = "400px";
        var title = this.translateService.instant('suppliers.fields.registration');
        var message = this.translateService.instant('suppliers.fields.registrationMsg');
        if (title.trim() !== '') {
            this.dialog.open(InfoDialogComponent, {
                data: {
                    title: title,
                    message: message,
                },
                width: width,
                maxWidth: width,
            });
        }
    };
    /**
     * open pop up info of Durc
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onInfoDurcPopUp = function () {
        var width = "400px";
        var title = this.translateService.instant('suppliers.fields.expirationDurc');
        var message = this.translateService.instant('suppliers.fields.expirationDurcMsg');
        if (title.trim() !== '') {
            this.dialog.open(InfoDialogComponent, {
                data: {
                    title: title,
                    message: message,
                },
                width: width,
                maxWidth: width,
            });
        }
    };
    /**
     * open pop up info of Registration
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onInfoCreditLicencePopUp = function () {
        var width = "400px";
        var title = this.translateService.instant('suppliers.fields.iCreditLicence');
        var message = this.translateService.instant('suppliers.fields.iCreditLicenceMsg');
        if (title.trim() !== '') {
            this.dialog.open(InfoDialogComponent, {
                data: {
                    title: title,
                    message: message,
                },
                width: width,
                maxWidth: width,
            });
        }
    };
    /**
     * open pop up info of Status
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onInfoStatusPopUp = function () {
        var width = "400px";
        var title = this.translateService.instant('suppliers.fields.iStatus');
        var message = this.translateService.instant('suppliers.fields.iStatusMsg');
        if (title.trim() !== '') {
            this.dialog.open(InfoDialogComponent, {
                data: {
                    title: title,
                    message: message,
                },
                width: width,
                maxWidth: width,
            });
        }
    };
    /**
     * Techincal file export in EXCEL format
     *
     * @since 1.0.0
     */
    CompanyDetailComponent.prototype.onExportTechnicalFile = function () {
        var _this = this;
        // this.exportTechnicalFileIsLoading = true;
        var date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
        var fileName = this.translateService.instant("companies.list.technicalFile") + "  " + this.companyData.name + " " + date;
        this.subscriptions.push(this.companiesService.getTechnicalFile(this.companyId).subscribe(function (res) {
            _this.toastrService.success(res.message);
        }, function (err) {
            _this.toastrService.error(err.message);
        })
        // this.companiesService.getTechnicalFile(companyId).subscribe(
        //   (res) => {
        //     const fileBlob = res;
        //     const blob = new Blob([fileBlob], {
        //       type: "application/vnd.ms-excel",
        //     });
        //     FileSaver.saveAs(blob, `${fileName}.xlsx`);
        //     this.exportTechnicalFileIsLoading = false;
        //   },
        //   (err) => {
        //     this.helperService.handleError(err);
        //     this.exportTechnicalFileIsLoading = false;
        //   }
        // )
        );
    };
    return CompanyDetailComponent;
}());
export { CompanyDetailComponent };
